<template>
  <div class="crm-root">

    <!-- ── 頂部工具欄 ── -->
    <div class="crm-toolbar">
      <div class="crm-tabs">
        <button class="crm-tab" :class="{ 'crm-tab--active': crmMainView==='dashboard' }" @click="switchToDashboard">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          總覽
        </button>
        <button class="crm-tab" :class="{ 'crm-tab--active': crmMainView==='customers' }" @click="crmMainView='customers'">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          客戶列表
        </button>
        <button class="crm-tab" :class="{ 'crm-tab--active': crmMainView==='pipeline' }" @click="switchToPipeline">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="4" height="18" rx="1"/><rect x="10" y="3" width="4" height="12" rx="1"/><rect x="17" y="3" width="4" height="8" rx="1"/></svg>
          商機漏斗
          <span v-if="pipelineSummary" class="crm-badge crm-badge--blue">{{ pipelineSummary.total_deals }}</span>
        </button>
        <button class="crm-tab" :class="{ 'crm-tab--active': crmMainView==='companies' }" @click="switchToCompanies">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
          公司管理
        </button>
      </div>

      <div class="crm-toolbar-right">
        <template v-if="crmMainView==='pipeline' && pipelineSummary">
          <div class="crm-kpi-inline">
            <span class="crm-kpi-inline__label">進行中</span>
            <span class="crm-kpi-inline__val crm-kpi-inline__val--blue">{{ formatMoney(pipelineSummary.total_pipeline) }}</span>
          </div>
          <div class="crm-kpi-inline">
            <span class="crm-kpi-inline__label">已成交</span>
            <span class="crm-kpi-inline__val crm-kpi-inline__val--green">{{ formatMoney(pipelineSummary.total_won) }}</span>
          </div>
          <button class="crm-btn crm-btn--outline crm-btn--sm" @click="crmExportPipelinePdf" :disabled="pipelinePdfLoading">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {{ pipelinePdfLoading ? '產生中…' : '匯出 PDF' }}
          </button>
        </template>
        <template v-if="crmMainView==='customers'">
          <button class="crm-btn crm-btn--outline crm-btn--sm" @click="exportCsv" title="匯出 CSV">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            CSV
          </button>
        </template>
      </div>
    </div>

    <!-- ── KPI 列（客戶視圖）── -->
    <div v-if="crmMainView==='customers' && stats" class="crm-kpi-bar">
      <div class="crm-kpi-card">
        <div class="crm-kpi-card__icon crm-kpi-card__icon--purple">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
        </div>
        <div>
          <div class="crm-kpi-card__num">{{ stats.total }}</div>
          <div class="crm-kpi-card__label">客戶總數</div>
        </div>
      </div>
      <div class="crm-kpi-card">
        <div class="crm-kpi-card__icon crm-kpi-card__icon--amber">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <div>
          <div class="crm-kpi-card__num">{{ stats.by_status?.lead ?? 0 }}</div>
          <div class="crm-kpi-card__label">潛在客戶</div>
        </div>
      </div>
      <div class="crm-kpi-card">
        <div class="crm-kpi-card__icon crm-kpi-card__icon--green">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <div class="crm-kpi-card__num">{{ stats.by_status?.customer ?? 0 }}</div>
          <div class="crm-kpi-card__label">已轉換客戶</div>
        </div>
      </div>
      <div class="crm-kpi-card">
        <div class="crm-kpi-card__icon crm-kpi-card__icon--blue">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
        </div>
        <div>
          <div class="crm-kpi-card__num">{{ stats.by_source?.email ?? 0 }}</div>
          <div class="crm-kpi-card__label">郵件來源</div>
        </div>
      </div>
      <div class="crm-kpi-card">
        <div class="crm-kpi-card__icon crm-kpi-card__icon--teal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        </div>
        <div>
          <div class="crm-kpi-card__num">{{ stats.by_source?.line ?? 0 }}</div>
          <div class="crm-kpi-card__label">LINE 來源</div>
        </div>
      </div>
    </div>

    <!-- ════════════════════ 總覽 Dashboard ════════════════════ -->
    <div v-if="crmMainView==='dashboard'" class="crm-dashboard">
      <div v-if="dashboardLoading" class="crm-loading"><div class="crm-spinner"></div> 載入中…</div>
      <template v-else-if="dashboardData">
        <!-- KPI 卡片 -->
        <div class="crm-dash-kpi">
          <div class="crm-dash-kpi-card">
            <div class="crm-dash-kpi-card__icon" style="background:#ede9fe;color:#7c3aed">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            </div>
            <div class="crm-dash-kpi-card__body">
              <div class="crm-dash-kpi-card__num">{{ dashboardData.kpi.total_contacts }}</div>
              <div class="crm-dash-kpi-card__label">客戶總數</div>
              <div class="crm-dash-kpi-card__sub">本月新增 +{{ dashboardData.kpi.new_this_month }}</div>
            </div>
          </div>
          <div class="crm-dash-kpi-card">
            <div class="crm-dash-kpi-card__icon" style="background:#dcfce7;color:#15803d">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div class="crm-dash-kpi-card__body">
              <div class="crm-dash-kpi-card__num">{{ dashboardData.kpi.total_customers }}</div>
              <div class="crm-dash-kpi-card__label">已轉換客戶</div>
              <div class="crm-dash-kpi-card__sub">贏率 {{ dashboardData.kpi.win_rate }}%</div>
            </div>
          </div>
          <div class="crm-dash-kpi-card">
            <div class="crm-dash-kpi-card__icon" style="background:#dbeafe;color:#1d4ed8">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div class="crm-dash-kpi-card__body">
              <div class="crm-dash-kpi-card__num">{{ formatMoney(dashboardData.kpi.pipeline_value) }}</div>
              <div class="crm-dash-kpi-card__label">進行中商機</div>
              <div class="crm-dash-kpi-card__sub">不含已成交/流失</div>
            </div>
          </div>
          <div class="crm-dash-kpi-card">
            <div class="crm-dash-kpi-card__icon" style="background:#fef3c7;color:#b45309">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            </div>
            <div class="crm-dash-kpi-card__body">
              <div class="crm-dash-kpi-card__num">{{ formatMoney(dashboardData.kpi.won_this_month) }}</div>
              <div class="crm-dash-kpi-card__label">本月成交金額</div>
              <div class="crm-dash-kpi-card__sub">本月結案</div>
            </div>
          </div>
        </div>

        <!-- 中段：漏斗圖 + 待辦跟進 -->
        <div class="crm-dash-main">
          <!-- 商機漏斗視覺化 -->
          <div class="crm-dash-section">
            <div class="crm-dash-section__title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="4" height="18" rx="1"/><rect x="10" y="3" width="4" height="12" rx="1"/><rect x="17" y="3" width="4" height="8" rx="1"/></svg>
              商機漏斗分佈
            </div>
            <div class="crm-dash-funnel">
              <div v-for="(s, key) in dashboardData.pipeline_stages" :key="key" class="crm-dash-funnel__row">
                <div class="crm-dash-funnel__label" :class="`crm-dash-funnel__label--${key}`">{{ stageLabel(key) }}</div>
                <div class="crm-dash-funnel__track">
                  <div class="crm-dash-funnel__bar" :class="`crm-dash-funnel__bar--${key}`"
                       :style="{ width: funnelWidth(key, s.count) }"></div>
                </div>
                <div class="crm-dash-funnel__meta">
                  <span class="crm-dash-funnel__count">{{ s.count }} 件</span>
                  <span v-if="s.total" class="crm-dash-funnel__amount">{{ formatMoney(s.total) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 到期跟進 -->
          <div class="crm-dash-section">
            <div class="crm-dash-section__title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              近期待辦跟進
            </div>
            <div v-if="!dashboardData.due_followups.length" class="crm-empty crm-empty--sm">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="20 6 9 17 4 12"/></svg>
              <p>目前沒有待辦跟進</p>
            </div>
            <div v-for="f in dashboardData.due_followups" :key="f.id" class="crm-dash-followup" :class="{ 'crm-dash-followup--overdue': f.overdue }">
              <div class="crm-dash-followup__icon">{{ f.overdue ? '⚠️' : '🔔' }}</div>
              <div class="crm-dash-followup__body">
                <div class="crm-dash-followup__note">{{ f.note || '未填提醒內容' }}</div>
                <div class="crm-dash-followup__time" :class="{ 'crm-dash-followup__time--overdue': f.overdue }">
                  {{ f.overdue ? '已逾期：' : '' }}{{ formatDate(f.due_at) }}
                </div>
              </div>
              <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="selectCustomerById(f.contact_id)">查看</button>
            </div>
          </div>
        </div>

        <!-- 最近活動 -->
        <div class="crm-dash-section crm-dash-section--full">
          <div class="crm-dash-section__title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            最近互動記錄
          </div>
          <div class="crm-dash-activity">
            <div v-for="i in dashboardData.recent_interactions" :key="i.id" class="crm-dash-activity__item">
              <div class="crm-dash-activity__icon" :style="typeIconStyle(i.type)">{{ typeIcon(i.type) }}</div>
              <div class="crm-dash-activity__body">
                <span :class="`crm-timeline__type--${i.type}`" style="font-size:.72rem;font-weight:700">{{ typeLabel(i.type) }}</span>
                <span v-if="i.subject" class="crm-dash-activity__subject">{{ i.subject }}</span>
              </div>
              <div class="crm-dash-activity__time">{{ formatDate(i.created_at) }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ════════════════════ 商機漏斗 ════════════════════ -->
    <div v-if="crmMainView==='pipeline'" class="crm-pipeline">
      <div v-if="pipelineLoading" class="crm-loading">
        <div class="crm-spinner"></div> 載入中…
      </div>
      <div v-else class="crm-pipeline__board">
        <div v-for="(col, stageKey) in pipeline" :key="stageKey" class="crm-pipeline__col">
          <!-- 欄位標題 -->
          <div class="crm-pipeline__col-head" :class="`crm-pipeline__col-head--${stageKey}`">
            <div class="crm-pipeline__col-head-inner">
              <span class="crm-pipeline__col-dot" :class="`crm-pipeline__col-dot--${stageKey}`"></span>
              <span class="crm-pipeline__col-title">{{ col.label }}</span>
              <span class="crm-badge crm-badge--count ml-auto">{{ col.count }}</span>
            </div>
            <div class="crm-pipeline__col-amount">{{ formatMoney(col.total_amount) }}</div>
          </div>

          <!-- 卡片區 -->
          <div
            class="crm-pipeline__cards"
            :class="{ 'crm-pipeline__cards--dragover': dragOverStage === stageKey && dragSrcStage !== stageKey }"
            @dragover="onDragOver(stageKey, $event)"
            @dragleave="onDragLeave(stageKey, $event)"
            @drop="onDrop(stageKey, $event)"
          >
            <div v-if="col.items.length===0" class="crm-pipeline__empty">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              <span>暫無商機</span>
            </div>

            <div
              v-for="opp in col.items"
              :key="opp.id"
              class="crm-opp-card"
              :class="{ 'crm-opp-card--dragging': dragOpp && dragOpp.id === opp.id }"
              draggable="true"
              @dragstart="onDragStart(opp, stageKey, $event)"
              @click="openPipelineCard(opp)"
            >
              <div class="crm-opp-card__title">
                {{ opp.title }}
                <span v-if="opp.priority==='high'" style="font-size:.65rem;color:#ef4444">🔴</span>
                <span v-else-if="opp.priority==='low'" style="font-size:.65rem;color:#16a34a">🟢</span>
              </div>
              <div class="crm-opp-card__contact">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                {{ opp.contact_name || opp.contact_email }}
              </div>
              <div v-if="opp.assigned_to" style="font-size:.68rem;color:var(--gray-400);margin-bottom:4px">
                👤 {{ opp.assigned_to.name || opp.assigned_to }}
              </div>
              <div v-if="opp.amount" class="crm-opp-card__amount" :class="`crm-opp-card__amount--${stageKey}`">
                {{ formatMoney(opp.amount) }}
                <span class="crm-opp-card__currency">{{ opp.currency }}</span>
              </div>
              <!-- AI 成交機率 -->
              <div class="crm-opp-card__ai-prob" v-if="opp.ai_win_prob !== null && opp.ai_win_prob !== undefined">
                <span class="crm-opp-card__ai-prob-label">🤖 AI：</span>
                <span class="crm-opp-card__ai-prob-val" :style="{ color: opp.ai_win_prob>=70 ? '#16a34a' : opp.ai_win_prob>=40 ? '#d97706' : '#ef4444' }">{{ opp.ai_win_prob }}%</span>
              </div>
              <div v-if="opp.expected_close_date" class="crm-opp-card__date" :class="{ 'crm-opp-card__date--overdue': isOverdue(opp.expected_close_date) }">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {{ isOverdue(opp.expected_close_date) ? '已逾期 · ' : '' }}{{ formatDateShort(opp.expected_close_date) }}
              </div>
              <!-- 快速移動 -->
              <div class="crm-opp-card__actions">
                <button
                  v-for="s in pipelineStages.filter(x=>x!==stageKey)"
                  :key="s"
                  class="crm-opp-card__move-btn"
                  @click.stop="moveStage(opp, s)"
                  :title="'移到：'+stageLabel(s)"
                >→ {{ stageShortLabel(s) }}</button>
              </div>
            </div>

            <button class="crm-pipeline__add-btn" @click="openAddOppInStage(stageKey)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              新增商機
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ════════════════════ 客戶列表 ════════════════════ -->
    <div v-if="crmMainView==='customers'" class="crm-layout">

      <!-- 左側：搜尋 + 列表 -->
      <div class="crm-sidebar">
        <div class="crm-sidebar__search">
          <div class="crm-search-wrap">
            <svg class="crm-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input v-model="searchQ" @input="onSearchInput" type="text" class="crm-search-input" placeholder="搜尋姓名、Email、公司…" />
          </div>
          <div class="crm-filters">
            <select v-model="filterSource" @change="loadCustomers(true)" class="crm-select">
              <option value="">所有來源</option>
              <option value="manual">手動建立</option>
              <option value="email">郵件</option>
              <option value="line">LINE</option>
            </select>
            <select v-model="filterStatus" @change="loadCustomers(true)" class="crm-select">
              <option value="">所有狀態</option>
              <option value="lead">潛在客戶</option>
              <option value="customer">客戶</option>
              <option value="inactive">非活躍</option>
            </select>
            <select v-model="filterAssignee" @change="loadCustomers(true)" class="crm-select" v-if="teamMembers.length">
              <option value="">所有業務</option>
              <option v-for="u in teamMembers" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
        </div>

        <!-- 批量工具列 -->
        <div v-if="selectedIds.size > 0" class="crm-bulk-bar">
          <span class="crm-bulk-bar__count">已選 {{ selectedIds.size }} 筆</span>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="showBulkTagDialog=true">加標籤</button>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="showBulkAssignDialog=true" v-if="teamMembers.length">改負責人</button>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="doBulkAction('change_status','customer')">設為客戶</button>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="doBulkExport">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            匯出
          </button>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="clearSelection">✕</button>
        </div>

        <!-- 批量加標籤 Dialog -->
        <div v-if="showBulkTagDialog" class="crm-bulk-dialog">
          <input v-model="bulkTagInput" class="crm-input crm-input--sm" placeholder="輸入標籤名稱" @keyup.enter="doBulkAction('add_tag', bulkTagInput)" />
          <button class="crm-btn crm-btn--primary crm-btn--xs" @click="doBulkAction('add_tag', bulkTagInput)">確認</button>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="showBulkTagDialog=false">取消</button>
        </div>

        <!-- 批量改負責人 Dialog -->
        <div v-if="showBulkAssignDialog" class="crm-bulk-dialog">
          <select v-model="bulkAssignId" class="crm-select crm-select--sm">
            <option :value="null">取消指派</option>
            <option v-for="u in teamMembers" :key="u.id" :value="u.id">{{ u.name }}</option>
          </select>
          <button class="crm-btn crm-btn--primary crm-btn--xs" @click="doBulkAction('assign_to', bulkAssignId)">確認</button>
          <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="showBulkAssignDialog=false">取消</button>
        </div>

        <!-- 列表 -->
        <div class="crm-sidebar__list">
          <!-- 全選 header -->
          <div v-if="customers.length > 0" class="crm-list-header">
            <label class="crm-checkbox-wrap" @click.stop>
              <input type="checkbox" :checked="selectedIds.size === customers.length && customers.length > 0"
                :indeterminate="selectedIds.size > 0 && selectedIds.size < customers.length"
                @change="toggleSelectAll" />
              <span class="crm-list-header__text">全選 ({{ total }})</span>
            </label>
          </div>

          <div v-if="loading" class="crm-loading crm-loading--sm">
            <div class="crm-spinner crm-spinner--sm"></div>
          </div>
          <div v-else-if="customers.length === 0" class="crm-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            <p>尚無客戶資料</p>
            <small>系統將自動從郵件和 LINE 蒐集</small>
          </div>

          <div
            v-for="c in customers"
            :key="c.id"
            class="crm-contact-item"
            :class="{ 'crm-contact-item--active': selected && selected.id === c.id, 'crm-contact-item--checked': selectedIds.has(c.id) }"
            @click="selectCustomer(c)"
          >
            <label class="crm-checkbox-wrap crm-contact-item__check" @click.stop>
              <input type="checkbox" :checked="selectedIds.has(c.id)" @change="toggleSelect(c.id)" />
            </label>
            <div class="crm-avatar" :class="avatarColorClass(c.name)">
              <img v-if="c.avatar_url" :src="c.avatar_url" class="crm-avatar__img" />
              <span v-else>{{ (c.name || '?')[0].toUpperCase() }}</span>
            </div>
            <div class="crm-contact-item__body">
              <div class="crm-contact-item__row1">
                <span class="crm-contact-item__name">{{ c.name }}</span>
                <span class="crm-badge" :class="statusBadgeClass(c.crm_status)">{{ statusLabel(c.crm_status) }}</span>
              </div>
              <div class="crm-contact-item__email">{{ c.email }}</div>
              <div v-if="c.company" class="crm-contact-item__company">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                {{ c.company }}
              </div>
              <div class="crm-contact-item__row3">
                <span class="crm-badge crm-badge--source" :class="sourceBadgeClass(c.source)">{{ sourceLabel(c.source) }}</span>
                <span v-if="c.assigned_to" class="crm-contact-item__assignee">👤 {{ c.assigned_to }}</span>
                <span v-if="c.last_interaction_at" class="crm-contact-item__time">{{ formatDate(c.last_interaction_at) }}</span>
              </div>
            </div>
          </div>

          <div v-if="!loading && customers.length < total" class="crm-load-more">
            <button @click="loadMore" class="crm-btn crm-btn--ghost crm-btn--sm w-full">
              載入更多（{{ customers.length }}/{{ total }}）
            </button>
          </div>
        </div>
      </div>

      <!-- 右側：詳細面板 -->
      <div class="crm-detail">
        <!-- 未選取 -->
        <div v-if="!selected" class="crm-detail__empty">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <h3>選取客戶查看詳情</h3>
          <p>從左側列表點選客戶，即可查看完整互動記錄與商機資訊</p>
        </div>

        <template v-else>
          <!-- Profile Header -->
          <div class="crm-profile-header">
            <div class="crm-profile-header__bg" :class="avatarColorClass(selected.name) + '--hero'"></div>
            <div class="crm-profile-header__content">
              <div class="crm-avatar crm-avatar--lg" :class="avatarColorClass(selected.name)">
                <img v-if="selected.avatar_url" :src="selected.avatar_url" class="crm-avatar__img" />
                <span v-else>{{ (selected.name || '?')[0].toUpperCase() }}</span>
              </div>
              <div class="crm-profile-header__info">
                <div class="crm-profile-header__name">{{ selected.name }}</div>
                <div class="crm-profile-header__meta">
                  <span v-if="selected.company">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                    {{ selected.company }}
                  </span>
                  <span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {{ selected.email }}
                  </span>
                  <span v-if="selected.phone">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    {{ selected.phone }}
                  </span>
                </div>
                <div v-if="selected.tags" class="crm-profile-header__tags">
                  <span v-for="tag in (selected.tags||'').split(',').filter(t=>t.trim())" :key="tag" class="crm-tag">{{ tag.trim() }}</span>
                </div>
              </div>
              <div class="crm-profile-header__actions">
                <select v-model="editStatus" @change="saveStatus" class="crm-select crm-select--status">
                  <option value="lead">潛在客戶</option>
                  <option value="customer">客戶</option>
                  <option value="inactive">非活躍</option>
                </select>
                <select v-if="teamMembers.length" v-model="editAssignee" @change="saveAssignee" class="crm-select crm-select--status">
                  <option :value="null">未指派</option>
                  <option v-for="u in teamMembers" :key="u.id" :value="u.id">👤 {{ u.name }}</option>
                </select>
                <button @click="openEditModal" class="crm-btn crm-btn--outline crm-btn--sm">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  編輯
                </button>
                <button @click="openCrmCompose" class="crm-btn crm-btn--primary crm-btn--sm">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  發信
                </button>
              </div>
            </div>
          </div>

          <!-- Lead Score Bar -->
          <div v-if="selected.lead_score !== undefined" class="crm-score-bar">
            <span class="crm-score-bar__label">潛在分數</span>
            <div class="crm-score-bar__track">
              <div class="crm-score-bar__fill" :style="{ width: Math.min(100, selected.lead_score) + '%' }"></div>
            </div>
            <span class="crm-score-bar__num">{{ selected.lead_score }}</span>
          </div>

          <!-- AI 客戶摘要卡 -->
          <div class="crm-ai-summary">
            <div class="crm-ai-summary__header">
              <span class="crm-ai-summary__title">🤖 AI 客戶摘要</span>
              <span v-if="selected.ai_summary_at" class="crm-ai-summary__date">{{ formatDateShort(selected.ai_summary_at) }}</span>
              <button class="crm-btn crm-btn--ghost crm-btn--xs" @click="generateAiSummary" :disabled="loadingAiSummary">
                {{ loadingAiSummary ? '生成中…' : selected.ai_summary ? '重新生成' : '生成摘要' }}
              </button>
            </div>
            <div v-if="loadingAiSummary" class="crm-ai-summary__loading">
              <div class="crm-spinner crm-spinner--sm"></div>
              <span>AI 分析中，約需 10-30 秒…</span>
            </div>
            <template v-else-if="selected.ai_summary">
              <ul class="crm-ai-summary__list">
                <li v-for="(item, i) in (selected.ai_summary.summary||[])" :key="i">{{ item }}</li>
              </ul>
              <div v-if="selected.ai_summary.key_concerns" class="crm-ai-summary__concern">
                <span class="crm-ai-summary__concern-label">核心痛點：</span>{{ selected.ai_summary.key_concerns }}
              </div>
              <div v-if="selected.ai_summary.last_commitment" class="crm-ai-summary__commitment">
                <span class="crm-ai-summary__concern-label">上次承諾：</span>{{ selected.ai_summary.last_commitment }}
              </div>
              <div v-if="selected.ai_summary.next_steps?.length" class="crm-ai-summary__next-steps">
                <div class="crm-ai-summary__concern-label" style="margin-bottom:4px">💡 下一步建議：</div>
                <ol class="crm-ai-summary__steps-list">
                  <li v-for="(s,i) in selected.ai_summary.next_steps" :key="i">{{ s }}</li>
                </ol>
              </div>
            </template>
            <div v-else class="crm-ai-summary__empty">點擊「生成摘要」，AI 將自動整理互動記錄與商機資訊</div>
          </div>

          <!-- Tabs -->
          <div class="crm-detail__tabs">
            <button class="crm-detail__tab" :class="{ 'crm-detail__tab--active': detailTab==='timeline' }" @click="detailTab='timeline'">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              互動時間軸
            </button>
            <button class="crm-detail__tab" :class="{ 'crm-detail__tab--active': detailTab==='opportunities' }" @click="switchTab('opportunities')">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              商機
              <span v-if="opportunities.length" class="crm-badge crm-badge--blue">{{ opportunities.length }}</span>
            </button>
            <button class="crm-detail__tab" :class="{ 'crm-detail__tab--active': detailTab==='followups' }" @click="switchTab('followups')">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              跟進提醒
              <span v-if="pendingFollowups.length" class="crm-badge crm-badge--amber">{{ pendingFollowups.length }}</span>
            </button>
            <button class="crm-detail__tab" :class="{ 'crm-detail__tab--active': detailTab==='info' }" @click="detailTab='info'">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              基本資料
            </button>
            <button class="crm-detail__tab" :class="{ 'crm-detail__tab--active': detailTab==='custom' }" @click="switchTab('custom')">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
              自定義欄位
            </button>
          </div>

          <!-- ── 互動時間軸 ── -->
          <div v-if="detailTab==='timeline'" class="crm-tab-body">
            <div class="crm-note-form">
              <div class="crm-note-form__label">記錄活動</div>
              <div class="crm-activity-type-bar">
                <button v-for="t in activityTypes" :key="t.type"
                  class="crm-activity-type-btn" :class="{ 'crm-activity-type-btn--active': newNoteType===t.type }"
                  @click="newNoteType=t.type">
                  {{ t.icon }} {{ t.label }}
                </button>
              </div>
              <input v-if="newNoteType !== 'note'" v-model="newNoteSubject" class="crm-input" style="margin-bottom:8px"
                :placeholder="newNoteType==='phone' ? '通話主旨（選填）' : newNoteType==='meeting' ? '會議主題（選填）' : '主旨'" />
              <textarea v-model="newNote" class="crm-textarea" :placeholder="activityPlaceholder" rows="3"></textarea>
              <div class="crm-note-form__footer">
                <button @click="submitNote" :disabled="!newNote.trim() || savingNote" class="crm-btn crm-btn--primary crm-btn--sm">
                  {{ savingNote ? '儲存中…' : '儲存' }}
                </button>
              </div>
            </div>

            <div v-if="detailLoading" class="crm-loading crm-loading--sm"><div class="crm-spinner crm-spinner--sm"></div></div>
            <div v-else-if="interactions.length===0" class="crm-empty crm-empty--sm">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              <p>尚無互動記錄</p>
            </div>

            <div class="crm-timeline">
              <div v-for="item in interactions" :key="item.id" class="crm-timeline__item">
                <div class="crm-timeline__dot-wrap">
                  <div class="crm-timeline__dot" :class="`crm-timeline__dot--${item.type}`">{{ typeIcon(item.type) }}</div>
                  <div class="crm-timeline__line"></div>
                </div>
                <div class="crm-timeline__card">
                  <div class="crm-timeline__card-head">
                    <span class="crm-timeline__type" :class="`crm-timeline__type--${item.type}`">{{ typeLabel(item.type) }}</span>
                    <span class="crm-timeline__time">{{ formatDate(item.created_at) }}</span>
                  </div>
                  <div v-if="item.subject" class="crm-timeline__subject">{{ item.subject }}</div>
                  <div v-if="item.content" class="crm-timeline__content">{{ item.content }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── 商機 ── -->
          <div v-if="detailTab==='opportunities'" class="crm-tab-body">
            <div class="crm-tab-body__header">
              <span class="crm-tab-body__title">商機列表</span>
              <button @click="openOppModal(null)" class="crm-btn crm-btn--primary crm-btn--sm">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                新增商機
              </button>
            </div>
            <div v-if="oppLoading" class="crm-loading crm-loading--sm"><div class="crm-spinner crm-spinner--sm"></div></div>
            <div v-else-if="opportunities.length===0" class="crm-empty crm-empty--sm">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              <p>尚無商機</p>
            </div>
            <div v-for="opp in opportunities" :key="opp.id" class="crm-opp-list-card">
              <div class="crm-opp-list-card__stage-bar" :class="`crm-opp-list-card__stage-bar--${opp.stage}`"></div>
              <div class="crm-opp-list-card__body">
                <div class="crm-opp-list-card__top">
                  <span class="crm-opp-list-card__title">{{ opp.title }}</span>
                  <span class="crm-badge" :class="stageBadgeClass(opp.stage)">{{ stageLabel(opp.stage) }}</span>
                </div>
                <div class="crm-opp-list-card__bottom">
                  <span v-if="opp.amount" class="crm-opp-list-card__amount">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    {{ Number(opp.amount).toLocaleString() }} {{ opp.currency }}
                  </span>
                  <span v-if="opp.expected_close_date" class="crm-opp-list-card__date">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    {{ formatDateShort(opp.expected_close_date) }}
                  </span>
                  <div class="crm-opp-list-card__btns">
                    <button @click="predictOppWinProb(opp)" class="crm-btn crm-btn--ghost crm-btn--xs"
                      :disabled="predictingOppId===opp.id" title="AI 成交機率預測">
                      {{ predictingOppId===opp.id ? '預測中…' : '🤖 預測' }}
                    </button>
                    <button @click="openOppModal(opp)" class="crm-btn crm-btn--ghost crm-btn--xs">編輯</button>
                    <button @click="deleteOpp(opp)" class="crm-btn crm-btn--ghost crm-btn--xs crm-btn--danger">刪除</button>
                  </div>
                </div>
                <!-- AI 成交機率結果 -->
                <div v-if="opp.ai_win_prob !== null && opp.ai_win_prob !== undefined" class="crm-opp-ai-result">
                  <span class="crm-opp-ai-result__prob" :style="{ color: opp.ai_win_prob>=70?'#16a34a':opp.ai_win_prob>=40?'#d97706':'#ef4444' }">
                    🤖 AI 預測成交率 {{ opp.ai_win_prob }}%
                  </span>
                  <span v-if="opp.ai_win_factors?.risk" class="crm-opp-ai-result__risk">⚠️ {{ opp.ai_win_factors.risk }}</span>
                </div>
                <div v-if="opp.notes" class="crm-opp-list-card__notes">{{ opp.notes }}</div>
              </div>
            </div>
          </div>

          <!-- ── 跟進提醒 ── -->
          <div v-if="detailTab==='followups'" class="crm-tab-body">
            <div class="crm-tab-body__header">
              <span class="crm-tab-body__title">跟進提醒</span>
              <button @click="openFollowupModal" class="crm-btn crm-btn--primary crm-btn--sm">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                新增提醒
              </button>
            </div>
            <div v-if="followupLoading" class="crm-loading crm-loading--sm"><div class="crm-spinner crm-spinner--sm"></div></div>
            <div v-else-if="pendingFollowups.length===0" class="crm-empty crm-empty--sm">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              <p>無待辦跟進</p>
            </div>
            <div v-for="f in pendingFollowups" :key="f.id" class="crm-followup-card" :class="{ 'crm-followup-card--overdue': isFollowupOverdue(f) }">
              <div class="crm-followup-card__icon">
                {{ isFollowupOverdue(f) ? '⚠️' : '🔔' }}
              </div>
              <div class="crm-followup-card__body">
                <div class="crm-followup-card__time" :class="{ 'crm-followup-card__time--overdue': isFollowupOverdue(f) }">
                  {{ isFollowupOverdue(f) ? '已逾期 · ' : '' }}{{ formatDateShort(f.due_at) }}
                </div>
                <div v-if="f.note" class="crm-followup-card__note">{{ f.note }}</div>
              </div>
              <div class="crm-followup-card__actions">
                <button @click="doneFollowup(f)" class="crm-btn crm-btn--success crm-btn--xs">完成</button>
                <button @click="deleteFollowup(f)" class="crm-btn crm-btn--ghost crm-btn--xs crm-btn--danger">刪除</button>
              </div>
            </div>
          </div>

          <!-- ── 基本資料 ── -->
          <div v-if="detailTab==='info'" class="crm-tab-body">
            <div class="crm-info-grid">
              <div class="crm-info-item">
                <div class="crm-info-item__label">姓名</div>
                <div class="crm-info-item__val">{{ selected.name || '—' }}</div>
              </div>
              <div class="crm-info-item">
                <div class="crm-info-item__label">Email</div>
                <div class="crm-info-item__val crm-info-item__val--mono">{{ selected.email }}</div>
              </div>
              <div class="crm-info-item">
                <div class="crm-info-item__label">電話</div>
                <div class="crm-info-item__val">{{ selected.phone || '—' }}</div>
              </div>
              <div class="crm-info-item">
                <div class="crm-info-item__label">公司</div>
                <div class="crm-info-item__val">{{ selected.company || '—' }}</div>
              </div>
              <div class="crm-info-item">
                <div class="crm-info-item__label">來源</div>
                <div><span class="crm-badge" :class="sourceBadgeClass(selected.source)">{{ sourceLabel(selected.source) }}</span></div>
              </div>
              <div class="crm-info-item">
                <div class="crm-info-item__label">狀態</div>
                <div><span class="crm-badge" :class="statusBadgeClass(selected.crm_status)">{{ statusLabel(selected.crm_status) }}</span></div>
              </div>
              <div v-if="selected.line_user_id" class="crm-info-item">
                <div class="crm-info-item__label">LINE ID</div>
                <div class="crm-info-item__val crm-info-item__val--mono">{{ selected.line_user_id }}</div>
              </div>
              <div class="crm-info-item">
                <div class="crm-info-item__label">建立時間</div>
                <div class="crm-info-item__val">{{ formatDate(selected.created_at) }}</div>
              </div>
            </div>
            <div v-if="selected.notes" class="crm-info-notes">
              <div class="crm-info-item__label">備注</div>
              <div class="crm-info-notes__text">{{ selected.notes }}</div>
            </div>
          </div>

          <!-- ── 自定義欄位 ── -->
          <div v-if="detailTab==='custom'" class="crm-tab-body">
            <div class="crm-tab-body__header">
              <span class="crm-tab-body__title">自定義欄位</span>
              <button class="crm-btn crm-btn--outline crm-btn--sm" @click="showCustomFieldAdmin=!showCustomFieldAdmin">
                ⚙️ 管理欄位
              </button>
            </div>

            <!-- 欄位管理 Panel -->
            <div v-if="showCustomFieldAdmin" class="crm-custom-admin">
              <div class="crm-custom-admin__head">
                <span class="crm-custom-admin__title">欄位定義</span>
              </div>
              <div v-for="fd in customFieldDefs" :key="fd.id" class="crm-custom-admin__row">
                <span class="crm-custom-admin__name">{{ fd.name }}</span>
                <span class="crm-custom-admin__key">{{ fd.field_key }}</span>
                <span class="crm-badge crm-badge--source">{{ fd.field_type }}</span>
                <button class="crm-btn crm-btn--ghost crm-btn--xs crm-btn--danger" @click="deleteCustomField(fd)">刪除</button>
              </div>
              <div class="crm-custom-admin__add">
                <input v-model="newFieldForm.name" class="crm-input crm-input--sm" placeholder="欄位名稱" style="flex:1.5" />
                <input v-model="newFieldForm.field_key" class="crm-input crm-input--sm" placeholder="field_key" style="flex:1" />
                <select v-model="newFieldForm.field_type" class="crm-select crm-select--sm" style="flex:1">
                  <option value="text">文字</option>
                  <option value="number">數字</option>
                  <option value="date">日期</option>
                  <option value="select">選單</option>
                  <option value="checkbox">勾選</option>
                </select>
                <button class="crm-btn crm-btn--primary crm-btn--xs" @click="createCustomField" :disabled="!newFieldForm.name||!newFieldForm.field_key">新增</button>
              </div>
              <div v-if="newFieldForm.field_type==='select'" class="crm-custom-admin__opts">
                <input v-model="newFieldForm.optionsRaw" class="crm-input crm-input--sm" placeholder="選項（用逗號分隔）" />
              </div>
            </div>

            <!-- 欄位值 -->
            <div v-if="customFieldDefs.length===0" class="crm-empty crm-empty--sm">
              <p>尚未建立自定義欄位</p>
              <small>點擊「管理欄位」新增</small>
            </div>
            <div v-for="fd in customFieldDefs" :key="'val-'+fd.id" class="crm-custom-field-row">
              <div class="crm-custom-field-row__label">{{ fd.name }}</div>
              <div class="crm-custom-field-row__input">
                <input v-if="fd.field_type==='text'" type="text"
                  :value="customFieldValues[fd.field_key]||''"
                  class="crm-input crm-input--sm"
                  @change="saveCustomField(fd.field_key, $event.target.value)" />
                <input v-else-if="fd.field_type==='number'" type="number"
                  :value="customFieldValues[fd.field_key]||''"
                  class="crm-input crm-input--sm"
                  @change="saveCustomField(fd.field_key, $event.target.value)" />
                <input v-else-if="fd.field_type==='date'" type="date"
                  :value="customFieldValues[fd.field_key]||''"
                  class="crm-input crm-input--sm"
                  @change="saveCustomField(fd.field_key, $event.target.value)" />
                <select v-else-if="fd.field_type==='select'"
                  :value="customFieldValues[fd.field_key]||''"
                  class="crm-select crm-select--sm"
                  @change="saveCustomField(fd.field_key, $event.target.value)">
                  <option value="">（請選擇）</option>
                  <option v-for="opt in (fd.options||[])" :key="opt" :value="opt">{{ opt }}</option>
                </select>
                <input v-else-if="fd.field_type==='checkbox'" type="checkbox"
                  :checked="!!customFieldValues[fd.field_key]"
                  @change="saveCustomField(fd.field_key, $event.target.checked)" />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ════════════════════ MODALS ════════════════════ -->

    <!-- ── CRM 發信 Modal ── -->
    <div v-if="showCrmCompose" class="crm-modal-backdrop" @click.self="showCrmCompose=false">
      <div class="crm-modal crm-modal--md">
        <div class="crm-modal__head">
          <span class="crm-modal__title">✉️ 發信給 {{ selected?.name }}</span>
          <button @click="showCrmCompose=false" class="crm-modal__close">×</button>
        </div>
        <div class="crm-modal__body">
          <div class="crm-form-row">
            <label class="crm-form-label">收件人</label>
            <input :value="selected?.email" class="crm-input" readonly style="background:var(--gray-50)" />
          </div>
          <div class="crm-form-row">
            <label class="crm-form-label">主旨 <span style="color:#ef4444">*</span></label>
            <input v-model="crmComposeSubject" class="crm-input" placeholder="郵件主旨" />
          </div>
          <div class="crm-form-row">
            <label class="crm-form-label">內容 <span style="color:#ef4444">*</span></label>
            <textarea v-model="crmComposeBody" class="crm-textarea" rows="8" placeholder="郵件內容…"></textarea>
          </div>
        </div>
        <div class="crm-modal__foot">
          <button @click="submitCrmEmail" :disabled="!crmComposeSubject.trim()||!crmComposeBody.trim()||sendingCrmEmail"
            class="crm-btn crm-btn--primary">
            {{ sendingCrmEmail ? '發送中…' : '發送' }}
          </button>
          <button @click="showCrmCompose=false" class="crm-btn crm-btn--outline">取消</button>
        </div>
      </div>
    </div>

    <!-- ════════════════════ 公司管理 ════════════════════ -->
    <div v-if="crmMainView==='companies'" class="crm-layout">
      <!-- 左：公司列表 -->
      <div class="crm-sidebar">
        <div class="crm-sidebar__search">
          <div class="crm-search-wrap">
            <svg class="crm-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input v-model="companySearchQ" @input="onCompanySearchInput" type="text" class="crm-search-input" placeholder="搜尋公司名稱、產業…" />
          </div>
          <button @click="openCompanyModal(null)" class="crm-btn crm-btn--primary crm-btn--sm w-full mt-2">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            新增公司
          </button>
        </div>
        <div class="crm-sidebar__list">
          <div v-if="companiesLoading" class="crm-loading crm-loading--sm"><div class="crm-spinner crm-spinner--sm"></div></div>
          <div v-else-if="companies.length === 0" class="crm-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            <p>尚無公司資料</p>
          </div>
          <div v-for="co in companies" :key="co.id" class="crm-contact-item"
               :class="{ 'crm-contact-item--active': selectedCompany && selectedCompany.id === co.id }"
               @click="selectCompany(co)">
            <div class="crm-avatar crm-avatar--indigo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            </div>
            <div class="crm-contact-item__body">
              <div class="crm-contact-item__row1">
                <span class="crm-contact-item__name">{{ co.name }}</span>
                <span class="crm-badge crm-badge--count">{{ co.contact_count }} 人</span>
              </div>
              <div v-if="co.industry" class="crm-contact-item__email">{{ co.industry }}</div>
              <div class="crm-contact-item__row3">
                <span v-if="co.size" class="crm-badge crm-badge--gray">{{ co.size }}</span>
                <span v-if="co.phone" class="crm-contact-item__time">{{ co.phone }}</span>
              </div>
            </div>
          </div>
          <div v-if="!companiesLoading && companies.length < companiesTotal" class="crm-load-more">
            <button @click="loadMoreCompanies" class="crm-btn crm-btn--ghost crm-btn--sm w-full">
              載入更多（{{ companies.length }}/{{ companiesTotal }}）
            </button>
          </div>
        </div>
      </div>

      <!-- 右：公司詳情 -->
      <div class="crm-detail">
        <div v-if="!selectedCompany" class="crm-detail__empty">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
          <h3>選取公司查看詳情</h3>
          <p>從左側列表選取公司，可查看聯絡人名單與完整資訊</p>
        </div>
        <template v-else>
          <!-- Header -->
          <div class="crm-profile-header">
            <div class="crm-profile-header__bg" style="background:linear-gradient(135deg,#e0e7ff,#dbeafe)"></div>
            <div class="crm-profile-header__content">
              <div class="crm-avatar crm-avatar--lg crm-avatar--indigo">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
              </div>
              <div class="crm-profile-header__info">
                <div class="crm-profile-header__name">{{ selectedCompany.name }}</div>
                <div class="crm-profile-header__meta">
                  <span v-if="selectedCompany.industry">🏭 {{ selectedCompany.industry }}</span>
                  <span v-if="selectedCompany.size">👥 {{ selectedCompany.size }}</span>
                  <span v-if="selectedCompany.phone">📞 {{ selectedCompany.phone }}</span>
                  <span v-if="selectedCompany.website">
                    <a :href="selectedCompany.website" target="_blank" style="color:var(--brand-600)">🌐 {{ selectedCompany.website }}</a>
                  </span>
                </div>
                <div v-if="selectedCompany.address" style="font-size:.78rem;color:var(--gray-500);margin-top:4px">📍 {{ selectedCompany.address }}</div>
              </div>
              <div class="crm-profile-header__actions">
                <button @click="openCompanyModal(selectedCompany)" class="crm-btn crm-btn--outline crm-btn--sm">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  編輯
                </button>
                <button @click="deleteCompany(selectedCompany)" class="crm-btn crm-btn--ghost crm-btn--sm crm-btn--danger">刪除</button>
              </div>
            </div>
          </div>

          <!-- 聯絡人列表 -->
          <div class="crm-tab-body">
            <div class="crm-tab-body__header">
              <span class="crm-tab-body__title">旗下聯絡人（{{ (selectedCompany.contacts||[]).length }}）</span>
            </div>
            <div v-if="companyDetailLoading" class="crm-loading crm-loading--sm"><div class="crm-spinner crm-spinner--sm"></div></div>
            <div v-else-if="!(selectedCompany.contacts||[]).length" class="crm-empty crm-empty--sm">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              <p>尚無關聯聯絡人</p>
            </div>
            <div v-for="c in (selectedCompany.contacts||[])" :key="c.id" class="crm-opp-list-card" style="cursor:pointer" @click="goToContact(c)">
              <div class="crm-opp-list-card__stage-bar" :class="statusBadgeClass(c.crm_status).replace('crm-badge--','crm-opp-list-card__stage-bar--')"></div>
              <div class="crm-opp-list-card__body">
                <div class="crm-opp-list-card__top">
                  <div class="crm-avatar" :class="avatarColorClass(c.name)" style="width:28px;height:28px;font-size:12px;flex-shrink:0">{{ (c.name||'?')[0].toUpperCase() }}</div>
                  <span class="crm-opp-list-card__title">{{ c.name }}</span>
                  <span class="crm-badge" :class="statusBadgeClass(c.crm_status)">{{ statusLabel(c.crm_status) }}</span>
                </div>
                <div class="crm-opp-list-card__bottom">
                  <span style="font-size:.72rem;color:var(--gray-500)">{{ c.email }}</span>
                  <span v-if="c.phone" style="font-size:.72rem;color:var(--gray-400)">{{ c.phone }}</span>
                </div>
              </div>
            </div>
            <div v-if="selectedCompany.notes" class="crm-info-notes" style="margin-top:8px">
              <div class="crm-tab-body__title" style="margin-bottom:6px">備注</div>
              <div class="crm-info-notes__text">{{ selectedCompany.notes }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 編輯客戶 Modal -->
    <div v-if="showEdit" class="crm-modal-backdrop" @click.self="showEdit=false">
      <div class="crm-modal">
        <div class="crm-modal__header">
          <h3>編輯客戶資料</h3>
          <button @click="showEdit=false" class="crm-modal__close">×</button>
        </div>
        <div class="crm-modal__body">
          <div class="crm-form-row">
            <label class="crm-label">姓名</label>
            <input v-model="editForm.name" class="crm-input" />
          </div>
          <div class="crm-form-row">
            <label class="crm-label">Email</label>
            <input v-model="editForm.email" class="crm-input" />
          </div>
          <div class="crm-form-grid">
            <div class="crm-form-row">
              <label class="crm-label">電話</label>
              <input v-model="editForm.phone" class="crm-input" />
            </div>
            <div class="crm-form-row">
              <label class="crm-label">公司</label>
              <input v-model="editForm.company" class="crm-input" />
            </div>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">標籤 <span class="crm-label__hint">逗號分隔</span></label>
            <input v-model="editForm.tags" class="crm-input" placeholder="VIP, 潛在, 重要客戶" />
          </div>
          <div class="crm-form-row">
            <label class="crm-label">備注</label>
            <textarea v-model="editForm.notes" class="crm-textarea" rows="3"></textarea>
          </div>
        </div>
        <div class="crm-modal__footer">
          <button @click="showEdit=false" class="crm-btn crm-btn--outline">取消</button>
          <button @click="saveEdit" :disabled="savingEdit" class="crm-btn crm-btn--primary">
            {{ savingEdit ? '儲存中…' : '儲存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 商機 Modal -->
    <div v-if="showOppModal" class="crm-modal-backdrop" @click.self="showOppModal=false">
      <div class="crm-modal">
        <div class="crm-modal__header">
          <h3>{{ oppForm.id ? '編輯商機' : '新增商機' }}</h3>
          <button @click="showOppModal=false" class="crm-modal__close">×</button>
        </div>
        <div class="crm-modal__body">
          <div v-if="_pipelineCardEdit && !oppForm.id" class="crm-form-row">
            <label class="crm-label">客戶 <span class="crm-label__required">*</span></label>
            <select v-model="oppForm.contact_id" class="crm-select crm-select--full">
              <option :value="null" disabled>請選擇客戶</option>
              <option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name || c.email }}</option>
            </select>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">商機標題 <span class="crm-label__required">*</span></label>
            <input v-model="oppForm.title" class="crm-input" placeholder="商機名稱" />
          </div>
          <div class="crm-form-grid">
            <div class="crm-form-row">
              <label class="crm-label">金額</label>
              <input v-model.number="oppForm.amount" type="number" class="crm-input" placeholder="0" />
            </div>
            <div class="crm-form-row">
              <label class="crm-label">幣別</label>
              <select v-model="oppForm.currency" class="crm-select crm-select--full">
                <option>TWD</option><option>USD</option><option>CNY</option><option>EUR</option>
              </select>
            </div>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">銷售階段</label>
            <select v-model="oppForm.stage" class="crm-select crm-select--full">
              <option value="prospect">初步接觸</option>
              <option value="proposal">提案中</option>
              <option value="negotiation">議價中</option>
              <option value="closed_won">成交</option>
              <option value="closed_lost">流失</option>
            </select>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">預計成交日</label>
            <input v-model="oppForm.expected_close_date" type="date" class="crm-input" />
          </div>
          <div class="crm-form-grid">
            <div class="crm-form-row">
              <label class="crm-label">競爭對手</label>
              <input v-model="oppForm.competitor" class="crm-input" placeholder="例：友商 A" />
            </div>
            <div class="crm-form-row">
              <label class="crm-label">優先級</label>
              <select v-model="oppForm.priority" class="crm-select crm-select--full">
                <option value="high">🔴 高</option>
                <option value="medium">🟡 中</option>
                <option value="low">🟢 低</option>
              </select>
            </div>
          </div>
          <div class="crm-form-row" v-if="teamMembers.length">
            <label class="crm-label">負責業務員</label>
            <select v-model="oppForm.assigned_to_user_id" class="crm-select crm-select--full">
              <option :value="null">未指派</option>
              <option v-for="u in teamMembers" :key="u.id" :value="u.id">{{ u.name }} ({{ u.email }})</option>
            </select>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">成交機率 <span style="color:var(--brand-600);font-weight:700">{{ oppForm.win_probability ?? 50 }}%</span></label>
            <input type="range" min="0" max="100" step="5" v-model.number="oppForm.win_probability" class="crm-range" />
          </div>
          <div class="crm-form-row">
            <label class="crm-label">產品 / 服務項目</label>
            <textarea v-model="oppForm.product_items" class="crm-textarea" rows="2" placeholder="列出報價項目，每行一項…"></textarea>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">備注</label>
            <textarea v-model="oppForm.notes" class="crm-textarea" rows="2"></textarea>
          </div>
        </div>
        <div class="crm-modal__footer">
          <button @click="showOppModal=false" class="crm-btn crm-btn--outline">取消</button>
          <button @click="saveOpp" :disabled="!oppForm.title || savingOpp" class="crm-btn crm-btn--primary">
            {{ savingOpp ? '儲存中…' : '儲存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 公司 Modal -->
    <div v-if="showCompanyModal" class="crm-modal-backdrop" @click.self="showCompanyModal=false">
      <div class="crm-modal">
        <div class="crm-modal__header">
          <h3>{{ companyForm.id ? '編輯公司' : '新增公司' }}</h3>
          <button @click="showCompanyModal=false" class="crm-modal__close">×</button>
        </div>
        <div class="crm-modal__body">
          <div class="crm-form-row">
            <label class="crm-label">公司名稱 <span class="crm-label__required">*</span></label>
            <input v-model="companyForm.name" class="crm-input" placeholder="例：震亞物聯網" />
          </div>
          <div class="crm-form-grid">
            <div class="crm-form-row">
              <label class="crm-label">產業別</label>
              <select v-model="companyForm.industry" class="crm-select crm-select--full">
                <option value="">請選擇</option>
                <option>資訊科技</option><option>製造業</option><option>金融服務</option>
                <option>零售電商</option><option>醫療健康</option><option>教育</option>
                <option>政府/公部門</option><option>物流運輸</option><option>其他</option>
              </select>
            </div>
            <div class="crm-form-row">
              <label class="crm-label">規模</label>
              <select v-model="companyForm.size" class="crm-select crm-select--full">
                <option value="">請選擇</option>
                <option>1–10 人</option><option>11–50 人</option>
                <option>51–200 人</option><option>200 人以上</option>
              </select>
            </div>
          </div>
          <div class="crm-form-grid">
            <div class="crm-form-row">
              <label class="crm-label">電話</label>
              <input v-model="companyForm.phone" class="crm-input" placeholder="02-XXXX-XXXX" />
            </div>
            <div class="crm-form-row">
              <label class="crm-label">網站</label>
              <input v-model="companyForm.website" class="crm-input" placeholder="https://…" />
            </div>
          </div>
          <div class="crm-form-row">
            <label class="crm-label">地址</label>
            <input v-model="companyForm.address" class="crm-input" placeholder="城市 / 完整地址" />
          </div>
          <div class="crm-form-row">
            <label class="crm-label">備注</label>
            <textarea v-model="companyForm.notes" class="crm-textarea" rows="2"></textarea>
          </div>
        </div>
        <div class="crm-modal__footer">
          <button @click="showCompanyModal=false" class="crm-btn crm-btn--outline">取消</button>
          <button @click="saveCompany" :disabled="!companyForm.name || savingCompany" class="crm-btn crm-btn--primary">
            {{ savingCompany ? '儲存中…' : '儲存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 跟進提醒 Modal -->
    <div v-if="showFollowupModal" class="crm-modal-backdrop" @click.self="showFollowupModal=false">
      <div class="crm-modal crm-modal--sm">
        <div class="crm-modal__header">
          <h3>新增跟進提醒</h3>
          <button @click="showFollowupModal=false" class="crm-modal__close">×</button>
        </div>
        <div class="crm-modal__body">
          <div class="crm-form-row">
            <label class="crm-label">提醒日期時間 <span class="crm-label__required">*</span></label>
            <input v-model="followupForm.due_at" type="datetime-local" class="crm-input" />
          </div>
          <div class="crm-form-row">
            <label class="crm-label">備注</label>
            <textarea v-model="followupForm.note" class="crm-textarea" rows="3" placeholder="跟進內容或提示…"></textarea>
          </div>
        </div>
        <div class="crm-modal__footer">
          <button @click="showFollowupModal=false" class="crm-btn crm-btn--outline">取消</button>
          <button @click="saveFollowup" :disabled="!followupForm.due_at || savingFollowup" class="crm-btn crm-btn--primary">
            {{ savingFollowup ? '設定中…' : '設定提醒' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useAppStore, ADMIN_API } from '../store/app.js'

const store = useAppStore()

// ── Main View Mode ─────────────────────────────────────────────────────────
const crmMainView = ref('dashboard')

// ── Dashboard ───────────────────────────────────────────────────────────────
const dashboardData = ref(null)
const dashboardLoading = ref(false)

async function switchToDashboard() {
  crmMainView.value = 'dashboard'
  if (dashboardData.value) return
  dashboardLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/crm/dashboard`)
    dashboardData.value = res.data
  } catch { window.$toast?.error('載入失敗', '無法載入總覽資料') } finally { dashboardLoading.value = false }
}

function funnelWidth(stage, count) {
  if (!dashboardData.value) return '0%'
  const stages = dashboardData.value.pipeline_stages
  const max = Math.max(1, ...Object.values(stages).map(s => s.count))
  return Math.round((count / max) * 100) + '%'
}

async function selectCustomerById(contactId) {
  crmMainView.value = 'customers'
  try {
    const res = await axios.get(`${ADMIN_API}/crm/customers/${contactId}`)
    const c = res.data
    customers.value = customers.value.length ? customers.value : [c]
    selected.value = c; editStatus.value = c.crm_status || 'lead'
    interactions.value = c.interactions || []
  } catch {}
}

// ── Pipeline State ─────────────────────────────────────────────────────────
const pipeline = ref({})
const pipelineSummary = ref(null)
const pipelineLoading = ref(false)
const pipelinePdfLoading = ref(false)
const pipelineStages = ['prospect', 'proposal', 'negotiation', 'closed_won', 'closed_lost']
const dragOpp = ref(null)
const dragSrcStage = ref(null)
const dragOverStage = ref(null)

async function loadPipeline() {
  pipelineLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/crm/pipeline`)
    pipeline.value = res.data.stages || {}
    pipelineSummary.value = res.data.summary || null
  } catch (e) {
    window.$toast?.error('載入失敗', '無法載入商機漏斗資料')
  } finally {
    pipelineLoading.value = false
  }
}

function switchToPipeline() {
  crmMainView.value = 'pipeline'
  loadPipeline()
}

async function crmExportPipelinePdf() {
  if (pipelinePdfLoading.value) return
  pipelinePdfLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/crm/pipeline/pdf`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `crm_pipeline_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    window.$toast?.error('匯出失敗', e.response?.data?.detail || '匯出失敗')
  } finally {
    pipelinePdfLoading.value = false
  }
}

async function moveStage(opp, newStage) {
  try {
    await axios.patch(`${ADMIN_API}/crm/opportunities/${opp.id}/stage`, { stage: newStage })
    await loadPipeline()
  } catch (e) {
    window.$toast?.error('移動失敗', '無法移動商機階段')
  }
}

function onDragStart(opp, stage, e) {
  dragOpp.value = opp; dragSrcStage.value = stage; e.dataTransfer.effectAllowed = 'move'
}
function onDragOver(stage, e) { e.preventDefault(); dragOverStage.value = stage }
function onDragLeave(stage, e) {
  if (e.relatedTarget && e.currentTarget && e.currentTarget.contains(e.relatedTarget)) return
  if (dragOverStage.value === stage) dragOverStage.value = null
}
async function onDrop(stage, e) {
  e.preventDefault(); dragOverStage.value = null
  const opp = dragOpp.value; const src = dragSrcStage.value
  dragOpp.value = null; dragSrcStage.value = null
  if (!opp || src === stage) return
  await moveStage(opp, stage)
}

function openPipelineCard(opp) {
  oppForm.value = { id: opp.id, contact_id: opp.contact_id, title: opp.title, amount: opp.amount,
    currency: opp.currency||'TWD', stage: opp.stage,
    expected_close_date: opp.expected_close_date ? opp.expected_close_date.substring(0,10) : '',
    notes: opp.notes||'', competitor: opp.competitor||'', win_probability: opp.win_probability??50,
    priority: opp.priority||'medium', product_items: opp.product_items||'',
    assigned_to_user_id: opp.assigned_to_user_id||null }
  showOppModal.value = true; _pipelineCardEdit.value = true
}

function openAddOppInStage(stage) {
  oppForm.value = { id: null, contact_id: null, title: '', amount: null, currency: 'TWD', stage, expected_close_date: '', notes: '',
    competitor:'', win_probability:50, priority:'medium', product_items:'', assigned_to_user_id:null }
  _pipelineCardEdit.value = true; showOppModal.value = true
}

const _pipelineCardEdit = ref(false)

function stageHeaderColor(s) {
  const c = { prospect:'var(--brand-400)', proposal:'var(--color-info)', negotiation:'var(--color-warning)', closed_won:'var(--color-success)', closed_lost:'var(--gray-400)' }
  return `background:${c[s]||'var(--gray-400)'};`
}
function stageTextColor(s) {
  const c = { prospect:'var(--brand-600)', proposal:'#1d4ed8', negotiation:'var(--color-warning)', closed_won:'var(--color-success)', closed_lost:'var(--gray-400)' }
  return c[s]||'var(--gray-600)'
}
function stageShortLabel(s) {
  return { prospect:'潛在', proposal:'提案', negotiation:'議價', closed_won:'✅', closed_lost:'❌' }[s]||s
}
function isOverdue(d) { return d ? new Date(d) < new Date() : false }
function formatMoney(v) {
  if (!v && v!==0) return '—'
  return 'NT$'+Number(v).toLocaleString('zh-TW',{maximumFractionDigits:0})
}

// ── Customers State ─────────────────────────────────────────────────────────
const customers = ref([])
const total = ref(0)
const loading = ref(false)
const stats = ref(null)
const searchQ = ref('')
const filterSource = ref('')
const filterStatus = ref('')
const filterAssignee = ref('')
const teamMembers = ref([])
const editAssignee = ref(null)
let searchTimer = null

// Item 8: 批量操作
const selectedIds = ref(new Set())
const showBulkTagDialog = ref(false)
const showBulkAssignDialog = ref(false)
const bulkTagInput = ref('')
const bulkAssignId = ref(null)

// Item 9: 自定義欄位
const customFieldDefs = ref([])
const customFieldValues = ref({})
const showCustomFieldAdmin = ref(false)
const newFieldForm = ref({ name:'', field_key:'', field_type:'text', optionsRaw:'' })

// Item 10: AI 摘要
const loadingAiSummary = ref(false)

// Item 11: AI 成交機率
const predictingOppId = ref(null)

// Item 13: CRM 發信
const showCrmCompose = ref(false)
const crmComposeSubject = ref('')
const crmComposeBody = ref('')
const sendingCrmEmail = ref(false)
const selected = ref(null)
const interactions = ref([])
const detailLoading = ref(false)
const detailTab = ref('timeline')
const newNote = ref('')
const newNoteType = ref('note')
const newNoteSubject = ref('')
const savingNote = ref(false)

const activityTypes = [
  { type:'note',     icon:'📝', label:'備注' },
  { type:'phone',    icon:'📞', label:'電話' },
  { type:'meeting',  icon:'🤝', label:'會議' },
  { type:'call_out', icon:'📤', label:'外撥' },
]
const activityPlaceholder = computed(() => {
  const m = { note:'輸入備注內容…', phone:'通話摘要、結論…', meeting:'會議記錄、決議事項…', call_out:'外撥結果、後續動作…' }
  return m[newNoteType.value] || '輸入內容…'
})
const showEdit = ref(false)
const editForm = ref({})
const savingEdit = ref(false)
const editStatus = ref('lead')
const offset = ref(0)
const PAGE = 50
const opportunities = ref([])
const oppLoading = ref(false)
const showOppModal = ref(false)
const oppForm = ref({})
const savingOpp = ref(false)
const pendingFollowups = ref([])
const followupLoading = ref(false)
const showFollowupModal = ref(false)
const followupForm = ref({ due_at:'', note:'' })
const savingFollowup = ref(false)

async function loadCustomers(reset=true) {
  if (reset) { offset.value=0; customers.value=[] }
  loading.value = true
  try {
    const params = { offset: offset.value, limit: PAGE }
    if (searchQ.value.trim()) params.q = searchQ.value.trim()
    if (filterSource.value) params.source = filterSource.value
    if (filterStatus.value) params.crm_status = filterStatus.value
    if (filterAssignee.value) params.assigned_to_user_id = filterAssignee.value
    const res = await axios.get(`${ADMIN_API}/crm/customers`, { params })
    const data = res.data||{}
    if (reset) customers.value = data.items||[]
    else customers.value.push(...(data.items||[]))
    total.value = data.total||0
  } catch { window.$toast?.error('載入失敗','無法載入客戶資料') } finally { loading.value=false }
}

async function loadStats() {
  try { const res = await axios.get(`${ADMIN_API}/crm/stats`); stats.value=res.data } catch {}
}

async function loadMore() { offset.value+=PAGE; await loadCustomers(false) }
function onSearchInput() { clearTimeout(searchTimer); searchTimer=setTimeout(()=>loadCustomers(true),350) }

async function selectCustomer(c) {
  selected.value=c; editStatus.value=c.crm_status||'lead'
  editAssignee.value = c.assigned_to_user_id || null
  detailTab.value='timeline'; interactions.value=[]; opportunities.value=[]; pendingFollowups.value=[]
  detailLoading.value=true
  try {
    const res = await axios.get(`${ADMIN_API}/crm/customers/${c.id}`)
    const data = res.data||{}; selected.value=data; editStatus.value=data.crm_status||'lead'
    editAssignee.value = data.assigned_to_user_id || null
    interactions.value=data.interactions||[]
  } catch {} finally { detailLoading.value=false }
}

async function switchTab(tab) {
  detailTab.value=tab
  if (tab==='opportunities'&&opportunities.value.length===0) await loadOpportunities()
  if (tab==='followups'&&pendingFollowups.value.length===0) await loadFollowups()
  if (tab==='custom') {
    if (!customFieldDefs.value.length) await loadCustomFieldDefs()
    customFieldValues.value = selected.value?.custom_fields || {}
  }
}

// ── Item 8: 批量操作 ──────────────────────────────────────────────────────────
function toggleSelect(id) {
  const s = new Set(selectedIds.value)
  s.has(id) ? s.delete(id) : s.add(id)
  selectedIds.value = s
}
function toggleSelectAll(e) {
  if (e.target.checked) {
    selectedIds.value = new Set(customers.value.map(c => c.id))
  } else {
    selectedIds.value = new Set()
  }
}
function clearSelection() {
  selectedIds.value = new Set()
  showBulkTagDialog.value = false
  showBulkAssignDialog.value = false
}
async function doBulkAction(action, value) {
  const ids = [...selectedIds.value]
  if (!ids.length) return
  try {
    await axios.post(`${ADMIN_API}/crm/customers/bulk`, { ids, action, value: value ?? null })
    showBulkTagDialog.value = false
    showBulkAssignDialog.value = false
    bulkTagInput.value = ''
    bulkAssignId.value = null
    clearSelection()
    await loadCustomers(true)
    window.$toast?.success('完成', `批量操作（${action}）已套用至 ${ids.length} 筆`)
  } catch (e) {
    window.$toast?.error('失敗', e.response?.data?.detail || '操作失敗')
  }
}
async function doBulkExport() {
  const ids = [...selectedIds.value]
  if (!ids.length) return
  try {
    const res = await axios.post(`${ADMIN_API}/crm/customers/bulk`, { ids, action:'export' }, { responseType:'blob' })
    const url = URL.createObjectURL(new Blob([res.data], { type:'text/csv' }))
    const a = document.createElement('a'); a.href=url; a.download='crm_selected.csv'; a.click()
    URL.revokeObjectURL(url)
    clearSelection()
  } catch { window.$toast?.error('匯出失敗','') }
}

// ── Item 9: 自定義欄位 ───────────────────────────────────────────────────────
async function loadCustomFieldDefs() {
  try {
    const res = await axios.get(`${ADMIN_API}/crm/custom-fields`)
    customFieldDefs.value = res.data || []
  } catch {}
}
async function createCustomField() {
  const form = newFieldForm.value
  if (!form.name || !form.field_key) return
  const payload = {
    name: form.name,
    field_key: form.field_key,
    field_type: form.field_type,
    options: form.field_type === 'select' && form.optionsRaw
      ? form.optionsRaw.split(',').map(s => s.trim()).filter(Boolean)
      : null,
  }
  try {
    const res = await axios.post(`${ADMIN_API}/crm/custom-fields`, payload)
    customFieldDefs.value.push(res.data)
    newFieldForm.value = { name:'', field_key:'', field_type:'text', optionsRaw:'' }
    window.$toast?.success('新增成功', `欄位 ${res.data.name} 已建立`)
  } catch (e) {
    window.$toast?.error('失敗', e.response?.data?.detail || '新增失敗')
  }
}
async function deleteCustomField(fd) {
  if (!confirm(`確定要刪除欄位「${fd.name}」嗎？`)) return
  try {
    await axios.delete(`${ADMIN_API}/crm/custom-fields/${fd.id}`)
    customFieldDefs.value = customFieldDefs.value.filter(f => f.id !== fd.id)
  } catch {}
}
async function saveCustomField(key, value) {
  if (!selected.value) return
  try {
    const res = await axios.patch(`${ADMIN_API}/crm/customers/${selected.value.id}/custom-fields`, { [key]: value })
    customFieldValues.value = res.data
    selected.value.custom_fields = res.data
  } catch {}
}

// ── Item 10: AI 客戶摘要 ──────────────────────────────────────────────────────
async function generateAiSummary() {
  if (!selected.value) return
  loadingAiSummary.value = true
  try {
    const res = await axios.post(`${ADMIN_API}/crm/customers/${selected.value.id}/ai-summary`)
    selected.value.ai_summary = res.data.ai_summary
    selected.value.ai_summary_at = res.data.generated_at
  } catch (e) {
    window.$toast?.error('AI 摘要失敗', e.response?.data?.detail || 'AI 服務暫時無法使用')
  } finally {
    loadingAiSummary.value = false
  }
}

// ── Item 11: AI 成交機率預測 ──────────────────────────────────────────────────
async function predictOppWinProb(opp) {
  predictingOppId.value = opp.id
  try {
    const res = await axios.post(`${ADMIN_API}/crm/opportunities/${opp.id}/ai-predict`)
    opp.ai_win_prob = res.data.ai_win_prob
    opp.ai_win_prob_at = res.data.generated_at
    opp.ai_win_factors = res.data.ai_win_factors
    // Also update pipeline if visible
    const col = pipelineData.value?.[opp.stage]
    if (col) {
      const idx = col.items?.findIndex(o => o.id === opp.id)
      if (idx >= 0) {
        col.items[idx].ai_win_prob = res.data.ai_win_prob
        col.items[idx].ai_win_factors = res.data.ai_win_factors
      }
    }
    window.$toast?.success('預測完成', `AI 預測成交率：${res.data.ai_win_prob}%`)
  } catch (e) {
    window.$toast?.error('預測失敗', e.response?.data?.detail || 'AI 服務暫時無法使用')
  } finally {
    predictingOppId.value = null
  }
}

// ── Item 13: CRM 直接發信 ─────────────────────────────────────────────────────
function openCrmCompose() {
  crmComposeSubject.value = ''
  crmComposeBody.value = ''
  showCrmCompose.value = true
}
async function submitCrmEmail() {
  if (!selected.value || !crmComposeSubject.value.trim() || !crmComposeBody.value.trim()) return
  sendingCrmEmail.value = true
  try {
    const res = await axios.post(`${ADMIN_API}/crm/customers/${selected.value.id}/send-email`, {
      subject: crmComposeSubject.value.trim(),
      body: crmComposeBody.value.trim(),
    })
    interactions.value.unshift(res.data)
    showCrmCompose.value = false
    window.$toast?.success('發送成功', `郵件已寄送給 ${selected.value.email}`)
  } catch (e) {
    window.$toast?.error('發送失敗', e.response?.data?.detail || '郵件發送失敗')
  } finally {
    sendingCrmEmail.value = false
  }
}

async function saveStatus() {
  if (!selected.value) return
  try {
    await axios.patch(`${ADMIN_API}/crm/customers/${selected.value.id}`, { crm_status:editStatus.value })
    selected.value.crm_status=editStatus.value
    const idx=customers.value.findIndex(c=>c.id===selected.value.id)
    if (idx>=0) customers.value[idx].crm_status=editStatus.value
  } catch {}
}

async function submitNote() {
  if (!newNote.value.trim()||!selected.value) return
  savingNote.value=true
  try {
    const payload = { content:newNote.value.trim(), type:newNoteType.value }
    if (newNoteSubject.value.trim()) payload.subject = newNoteSubject.value.trim()
    const res = await axios.post(`${ADMIN_API}/crm/customers/${selected.value.id}/notes`, payload)
    interactions.value.unshift(res.data)
    newNote.value=''; newNoteSubject.value=''; newNoteType.value='note'
  } catch {} finally { savingNote.value=false }
}

async function loadTeam() {
  if (teamMembers.value.length) return
  try {
    const res = await axios.get(`${ADMIN_API}/crm/team`)
    teamMembers.value = res.data || []
  } catch {}
}

async function saveAssignee() {
  if (!selected.value) return
  try {
    await axios.patch(`${ADMIN_API}/crm/customers/${selected.value.id}`, { assigned_to_user_id: editAssignee.value || null })
    const u = teamMembers.value.find(m => m.id === editAssignee.value)
    selected.value.assigned_to = u ? u.name : null
    selected.value.assigned_to_user_id = editAssignee.value
  } catch { window.$toast?.error('失敗','指派業務員失敗') }
}

function openEditModal() {
  editForm.value={ name:selected.value.name, email:selected.value.email, phone:selected.value.phone||'',
    company:selected.value.company||'', tags:selected.value.tags||'', notes:selected.value.notes||'' }
  showEdit.value=true
}

async function saveEdit() {
  if (!selected.value) return; savingEdit.value=true
  try {
    const res = await axios.patch(`${ADMIN_API}/crm/customers/${selected.value.id}`,editForm.value)
    Object.assign(selected.value,res.data)
    const idx=customers.value.findIndex(c=>c.id===selected.value.id)
    if (idx>=0) Object.assign(customers.value[idx],res.data)
    showEdit.value=false
  } catch {} finally { savingEdit.value=false }
}

async function loadOpportunities() {
  if (!selected.value) return; oppLoading.value=true
  try { const res=await axios.get(`${ADMIN_API}/crm/customers/${selected.value.id}/opportunities`); opportunities.value=res.data||[] }
  catch {} finally { oppLoading.value=false }
}

function openOppModal(opp) {
  if (opp) {
    oppForm.value={ id:opp.id, title:opp.title, amount:opp.amount||'', currency:opp.currency||'TWD',
      stage:opp.stage||'prospect', expected_close_date:opp.expected_close_date?opp.expected_close_date.slice(0,10):'',
      notes:opp.notes||'', competitor:opp.competitor||'', win_probability:opp.win_probability??50,
      priority:opp.priority||'medium', product_items:opp.product_items||'' }
  } else {
    oppForm.value={ title:'', amount:'', currency:'TWD', stage:'prospect', expected_close_date:'',
      notes:'', competitor:'', win_probability:50, priority:'medium', product_items:'' }
  }
  showOppModal.value=true
}

async function saveOpp() {
  const contactId=oppForm.value.contact_id||(selected.value?.id)
  if (!oppForm.value.title) return; savingOpp.value=true
  try {
    const payload={ title:oppForm.value.title, amount:oppForm.value.amount||null, currency:oppForm.value.currency,
      stage:oppForm.value.stage, expected_close_date:oppForm.value.expected_close_date||null,
      notes:oppForm.value.notes||null, competitor:oppForm.value.competitor||null,
      win_probability:oppForm.value.win_probability??null, priority:oppForm.value.priority||'medium',
      product_items:oppForm.value.product_items||null }
    if (oppForm.value.id) {
      const res=await axios.patch(`${ADMIN_API}/crm/opportunities/${oppForm.value.id}`,payload)
      const idx=opportunities.value.findIndex(o=>o.id===oppForm.value.id)
      if (idx>=0) opportunities.value[idx]=res.data
    } else if (contactId) {
      const res=await axios.post(`${ADMIN_API}/crm/customers/${contactId}/opportunities`,payload)
      opportunities.value.unshift(res.data)
    }
    showOppModal.value=false
    if (crmMainView.value==='pipeline') loadPipeline()
  } catch {} finally { savingOpp.value=false; _pipelineCardEdit.value=false }
}

async function deleteOpp(opp) {
  if (!confirm(`確定刪除商機「${opp.title}」？`)) return
  await axios.delete(`${ADMIN_API}/crm/opportunities/${opp.id}`)
  opportunities.value=opportunities.value.filter(o=>o.id!==opp.id)
}

async function loadFollowups() {
  if (!selected.value) return; followupLoading.value=true
  try { const res=await axios.get(`${ADMIN_API}/crm/customers/${selected.value.id}/followups`); pendingFollowups.value=res.data||[] }
  catch {} finally { followupLoading.value=false }
}

function openFollowupModal() {
  const t=new Date(); t.setDate(t.getDate()+1)
  followupForm.value={ due_at:t.toISOString().slice(0,16), note:'' }
  showFollowupModal.value=true
}

async function saveFollowup() {
  if (!followupForm.value.due_at||!selected.value) return; savingFollowup.value=true
  try {
    const res=await axios.post(`${ADMIN_API}/crm/customers/${selected.value.id}/followups`,
      { due_at:new Date(followupForm.value.due_at).toISOString(), note:followupForm.value.note||null })
    pendingFollowups.value.push(res.data)
    pendingFollowups.value.sort((a,b)=>new Date(a.due_at)-new Date(b.due_at))
    showFollowupModal.value=false
  } catch {} finally { savingFollowup.value=false }
}

async function doneFollowup(f) {
  await axios.patch(`${ADMIN_API}/crm/followups/${f.id}/done`)
  pendingFollowups.value=pendingFollowups.value.filter(x=>x.id!==f.id)
}

async function deleteFollowup(f) {
  await axios.delete(`${ADMIN_API}/crm/followups/${f.id}`)
  pendingFollowups.value=pendingFollowups.value.filter(x=>x.id!==f.id)
}

function isFollowupOverdue(f) { return new Date(f.due_at)<new Date() }

async function exportCsv() {
  const params=new URLSearchParams()
  if (filterSource.value) params.set('source',filterSource.value)
  if (filterStatus.value) params.set('crm_status',filterStatus.value)
  try {
    const res=await axios.get(`${ADMIN_API}/crm/customers/export.csv?${params}`,{responseType:'blob'})
    const url=URL.createObjectURL(res.data); const a=document.createElement('a')
    a.href=url; a.download='crm_customers.csv'; a.click(); URL.revokeObjectURL(url)
  } catch {}
}

// ── Helpers ─────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['--av-purple','--av-blue','--av-teal','--av-green','--av-amber','--av-rose','--av-indigo']
function avatarColorClass(name) {
  const i = name ? name.charCodeAt(0) % AVATAR_COLORS.length : 0
  return `crm-avatar--${AVATAR_COLORS[i].replace('--av-','')}`
}
function sourceLabel(s) { return {manual:'手動',email:'郵件',line:'LINE'}[s]||s||'—' }
function sourceBadgeClass(s) { return {manual:'crm-badge--gray',email:'crm-badge--blue',line:'crm-badge--green'}[s]||'crm-badge--gray' }
function statusLabel(s) { return {lead:'潛在客戶',customer:'客戶',inactive:'非活躍'}[s]||s||'—' }
function statusBadgeClass(s) { return {lead:'crm-badge--amber',customer:'crm-badge--green',inactive:'crm-badge--gray'}[s]||'' }
function typeLabel(t) { return {email_in:'收到郵件',email_out:'發送郵件',line_msg:'LINE 訊息',line_msg_out:'回覆 LINE',note:'備注'}[t]||t }
function typeIcon(t) { return {email_in:'📩',email_out:'📤',line_msg:'💬',line_msg_out:'💬',note:'📝'}[t]||'•' }
function typeIconStyle(t) {
  const map={email_in:'background:var(--brand-50)',email_out:'background:#eef2ff',line_msg:'background:#f0fdf4',line_msg_out:'background:#f0fdf4',note:'background:#fffbeb'}
  return map[t]||'background:var(--gray-50)'
}
function typeColor(t) {
  const map={email_in:'color:var(--brand-600)',email_out:'color:#4f46e5',line_msg:'color:#16a34a',note:'color:#d97706'}
  return map[t]||'color:var(--gray-600)'
}
function formatDate(d) {
  if (!d) return '—'
  try { return new Date(d).toLocaleString('zh-TW',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'}) } catch { return d }
}
function formatDateShort(d) {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString('zh-TW') } catch { return d }
}
function stageLabel(s) {
  return {prospect:'初步接觸',proposal:'提案中',negotiation:'議價中',closed_won:'✅ 成交',closed_lost:'❌ 流失'}[s]||s
}
function stageBadgeClass(s) {
  return {prospect:'crm-badge--blue',proposal:'crm-badge--purple',negotiation:'crm-badge--amber',closed_won:'crm-badge--green',closed_lost:'crm-badge--gray'}[s]||'crm-badge--blue'
}

// ── Companies ────────────────────────────────────────────────────────────────
const companies = ref([])
const companiesTotal = ref(0)
const companiesLoading = ref(false)
const companySearchQ = ref('')
const selectedCompany = ref(null)
const companyDetailLoading = ref(false)
const showCompanyModal = ref(false)
const companyForm = ref({})
const savingCompany = ref(false)
let companySearchTimer = null

async function loadCompanies(reset=true) {
  if (reset) { companies.value=[]; companiesOffset.value=0 }
  companiesLoading.value=true
  try {
    const params={ offset:companiesOffset.value, limit:50 }
    if (companySearchQ.value.trim()) params.q=companySearchQ.value.trim()
    const res=await axios.get(`${ADMIN_API}/crm/companies`,{params})
    if (reset) companies.value=res.data.items||[]
    else companies.value.push(...(res.data.items||[]))
    companiesTotal.value=res.data.total||0
  } catch {} finally { companiesLoading.value=false }
}
const companiesOffset = ref(0)
function onCompanySearchInput() { clearTimeout(companySearchTimer); companySearchTimer=setTimeout(()=>loadCompanies(true),350) }
async function loadMoreCompanies() { companiesOffset.value+=50; await loadCompanies(false) }

async function selectCompany(co) {
  selectedCompany.value=co; companyDetailLoading.value=true
  try {
    const res=await axios.get(`${ADMIN_API}/crm/companies/${co.id}`)
    selectedCompany.value=res.data
  } catch {} finally { companyDetailLoading.value=false }
}

function openCompanyModal(co) {
  if (co) {
    companyForm.value={ id:co.id, name:co.name, industry:co.industry||'', size:co.size||'',
      website:co.website||'', phone:co.phone||'', address:co.address||'', notes:co.notes||'' }
  } else {
    companyForm.value={ id:null, name:'', industry:'', size:'', website:'', phone:'', address:'', notes:'' }
  }
  showCompanyModal.value=true
}

async function saveCompany() {
  if (!companyForm.value.name) return; savingCompany.value=true
  try {
    const payload={ name:companyForm.value.name.trim(), industry:companyForm.value.industry||null,
      size:companyForm.value.size||null, website:companyForm.value.website||null,
      phone:companyForm.value.phone||null, address:companyForm.value.address||null,
      notes:companyForm.value.notes||null }
    if (companyForm.value.id) {
      const res=await axios.patch(`${ADMIN_API}/crm/companies/${companyForm.value.id}`,payload)
      const idx=companies.value.findIndex(c=>c.id===companyForm.value.id)
      if (idx>=0) companies.value[idx]=res.data
      if (selectedCompany.value?.id===companyForm.value.id) selectedCompany.value=res.data
    } else {
      const res=await axios.post(`${ADMIN_API}/crm/companies`,payload)
      companies.value.unshift(res.data); companiesTotal.value++
    }
    showCompanyModal.value=false
  } catch (e) { window.$toast?.error('儲存失敗',e.response?.data?.detail||'') } finally { savingCompany.value=false }
}

async function deleteCompany(co) {
  if (!confirm(`確定刪除公司「${co.name}」？關聯聯絡人的公司欄位將被清除。`)) return
  await axios.delete(`${ADMIN_API}/crm/companies/${co.id}`)
  companies.value=companies.value.filter(c=>c.id!==co.id)
  if (selectedCompany.value?.id===co.id) selectedCompany.value=null
}

function goToContact(c) {
  crmMainView.value='customers'
  selectCustomer(c)
}

async function switchToCompanies() {
  crmMainView.value='companies'
  if (!companies.value.length) await loadCompanies()
}

onMounted(()=>{ switchToDashboard(); loadCustomers(); loadStats(); loadTeam() })
</script>

<style scoped>
/* ── Root ── */
.crm-root { display:flex; flex-direction:column; height:100%; overflow:hidden; background:var(--gray-50,#f8fafc); }

/* ── Toolbar ── */
.crm-toolbar { display:flex; align-items:center; gap:12px; padding:10px 16px; background:var(--surface,#fff); border-bottom:1px solid var(--border,#e5e7eb); flex-shrink:0; }
.crm-tabs { display:flex; gap:4px; }
.crm-tab { display:flex; align-items:center; gap:6px; padding:6px 14px; border-radius:8px; font-size:0.82rem; font-weight:500; color:var(--gray-600,#4b5563); background:transparent; border:none; cursor:pointer; transition:background .15s,color .15s; }
.crm-tab:hover { background:var(--gray-100,#f3f4f6); color:var(--gray-800,#1f2937); }
.crm-tab--active { background:var(--brand-50,#eef2ff); color:var(--brand-700,#4338ca); font-weight:600; }
.crm-toolbar-right { display:flex; align-items:center; gap:10px; margin-left:auto; }

/* ── KPI inline ── */
.crm-kpi-inline { display:flex; flex-direction:column; align-items:flex-end; }
.crm-kpi-inline__label { font-size:0.65rem; color:var(--gray-400,#9ca3af); }
.crm-kpi-inline__val { font-size:0.82rem; font-weight:700; }
.crm-kpi-inline__val--blue { color:var(--brand-600,#4f46e5); }
.crm-kpi-inline__val--green { color:#16a34a; }

/* ── KPI bar ── */
.crm-kpi-bar { display:flex; gap:1px; background:var(--border,#e5e7eb); border-bottom:1px solid var(--border,#e5e7eb); flex-shrink:0; }
.crm-kpi-card { flex:1; display:flex; align-items:center; gap:12px; padding:12px 16px; background:var(--surface,#fff); transition:background .15s; }
.crm-kpi-card:hover { background:var(--gray-50,#f8fafc); }
.crm-kpi-card__icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.crm-kpi-card__icon--purple { background:#f3e8ff; color:#7c3aed; }
.crm-kpi-card__icon--amber  { background:#fef3c7; color:#d97706; }
.crm-kpi-card__icon--green  { background:#dcfce7; color:#16a34a; }
.crm-kpi-card__icon--blue   { background:#dbeafe; color:#2563eb; }
.crm-kpi-card__icon--teal   { background:#ccfbf1; color:#0d9488; }
.crm-kpi-card__num { font-size:1.3rem; font-weight:800; color:var(--gray-900,#111827); line-height:1; }
.crm-kpi-card__label { font-size:0.72rem; color:var(--gray-500,#6b7280); margin-top:2px; }

/* ── Pipeline ── */
.crm-pipeline { flex:1; overflow:hidden; }
.crm-pipeline__board { display:flex; height:100%; overflow-x:auto; }
.crm-pipeline__col { min-width:260px; max-width:300px; flex:1; display:flex; flex-direction:column; border-right:1px solid var(--border,#e5e7eb); background:var(--surface,#fff); }
.crm-pipeline__col-head { padding:14px 14px 10px; border-bottom:1px solid var(--border,#e5e7eb); flex-shrink:0; }
.crm-pipeline__col-head--prospect    { border-top:3px solid #818cf8; }
.crm-pipeline__col-head--proposal    { border-top:3px solid #38bdf8; }
.crm-pipeline__col-head--negotiation { border-top:3px solid #fbbf24; }
.crm-pipeline__col-head--closed_won  { border-top:3px solid #34d399; }
.crm-pipeline__col-head--closed_lost { border-top:3px solid #d1d5db; }
.crm-pipeline__col-head-inner { display:flex; align-items:center; gap:8px; }
.crm-pipeline__col-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.crm-pipeline__col-dot--prospect    { background:#818cf8; }
.crm-pipeline__col-dot--proposal    { background:#38bdf8; }
.crm-pipeline__col-dot--negotiation { background:#fbbf24; }
.crm-pipeline__col-dot--closed_won  { background:#34d399; }
.crm-pipeline__col-dot--closed_lost { background:#d1d5db; }
.crm-pipeline__col-title { font-size:0.82rem; font-weight:700; color:var(--gray-800,#1f2937); }
.crm-pipeline__col-amount { font-size:0.72rem; color:var(--gray-500,#6b7280); margin-top:4px; padding-left:16px; }
.crm-pipeline__cards { flex:1; overflow-y:auto; padding:10px; display:flex; flex-direction:column; gap:8px; transition:background .15s; }
.crm-pipeline__cards--dragover { background:#eef2ff; outline:2px dashed #818cf8; outline-offset:-4px; border-radius:8px; }
.crm-pipeline__empty { display:flex; flex-direction:column; align-items:center; gap:6px; padding:28px 0; color:var(--gray-300,#d1d5db); font-size:0.78rem; }
.crm-pipeline__add-btn { display:flex; align-items:center; justify-content:center; gap:6px; padding:8px; border-radius:8px; border:1px dashed var(--border,#e5e7eb); background:transparent; color:var(--gray-400,#9ca3af); font-size:0.78rem; cursor:pointer; transition:all .15s; margin-top:4px; }
.crm-pipeline__add-btn:hover { background:var(--gray-50,#f8fafc); color:var(--brand-600,#4f46e5); border-color:var(--brand-300,#a5b4fc); }

/* ── Opp Card (pipeline) ── */
.crm-opp-card { background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:10px; padding:12px 14px; cursor:grab; transition:box-shadow .15s,transform .15s; }
.crm-opp-card:hover { box-shadow:0 4px 16px rgba(0,0,0,0.1); transform:translateY(-1px); }
.crm-opp-card--dragging { opacity:0.4; }
.crm-opp-card__title { font-size:0.82rem; font-weight:700; color:var(--gray-900,#111827); margin-bottom:4px; }
.crm-opp-card__contact { display:flex; align-items:center; gap:4px; font-size:0.72rem; color:var(--gray-500,#6b7280); margin-bottom:6px; }
.crm-opp-card__amount { font-size:0.85rem; font-weight:700; margin-bottom:4px; }
.crm-opp-card__amount--prospect    { color:#4f46e5; }
.crm-opp-card__amount--proposal    { color:#0369a1; }
.crm-opp-card__amount--negotiation { color:#b45309; }
.crm-opp-card__amount--closed_won  { color:#15803d; }
.crm-opp-card__amount--closed_lost { color:var(--gray-400,#9ca3af); }
.crm-opp-card__currency { font-size:0.65rem; font-weight:400; margin-left:2px; }
.crm-opp-card__date { display:flex; align-items:center; gap:4px; font-size:0.7rem; color:var(--gray-400,#9ca3af); margin-bottom:8px; }
.crm-opp-card__date--overdue { color:#ef4444; }
.crm-opp-card__actions { display:flex; gap:4px; flex-wrap:wrap; }
.crm-opp-card__move-btn { font-size:0.62rem; padding:2px 7px; border-radius:4px; border:1px solid var(--border,#e5e7eb); background:transparent; color:var(--gray-500,#6b7280); cursor:pointer; transition:all .1s; }
.crm-opp-card__move-btn:hover { background:var(--brand-50,#eef2ff); color:var(--brand-600,#4f46e5); border-color:var(--brand-300,#a5b4fc); }

/* ── Layout ── */
.crm-layout { display:flex; flex:1; overflow:hidden; }

/* ── Sidebar ── */
.crm-sidebar { width:300px; min-width:240px; max-width:360px; border-right:1px solid var(--border,#e5e7eb); display:flex; flex-direction:column; background:var(--surface,#fff); }
.crm-sidebar__search { padding:12px; border-bottom:1px solid var(--border,#e5e7eb); }
.crm-search-wrap { position:relative; margin-bottom:8px; }
.crm-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--gray-400,#9ca3af); pointer-events:none; }
.crm-search-input { width:100%; padding:8px 10px 8px 32px; border-radius:8px; border:1px solid var(--border,#e5e7eb); font-size:0.82rem; background:var(--gray-50,#f8fafc); color:var(--gray-800,#1f2937); outline:none; transition:border-color .15s; }
.crm-search-input:focus { border-color:var(--brand-400,#818cf8); background:#fff; }
.crm-filters { display:flex; gap:6px; }
.crm-sidebar__list { flex:1; overflow-y:auto; }

/* ── Contact Item ── */
.crm-contact-item { display:flex; align-items:flex-start; gap:10px; padding:11px 14px; border-bottom:1px solid var(--border,#e5e7eb); cursor:pointer; transition:background .12s; }
.crm-contact-item:hover { background:var(--gray-50,#f8fafc); }
.crm-contact-item--active { background:var(--brand-50,#eef2ff); border-left:3px solid var(--brand-500,#6366f1); }
.crm-contact-item__body { flex:1; min-width:0; }
.crm-contact-item__row1 { display:flex; align-items:center; gap:6px; margin-bottom:2px; }
.crm-contact-item__name { font-size:0.84rem; font-weight:700; color:var(--gray-800,#1f2937); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; flex:1; }
.crm-contact-item__email { font-size:0.72rem; color:var(--gray-500,#6b7280); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:2px; }
.crm-contact-item__company { display:flex; align-items:center; gap:3px; font-size:0.7rem; color:var(--gray-400,#9ca3af); margin-bottom:4px; }
.crm-contact-item__row3 { display:flex; align-items:center; gap:6px; }
.crm-contact-item__time { font-size:0.65rem; color:var(--gray-400,#9ca3af); margin-left:auto; }

/* ── Avatar ── */
.crm-avatar { width:38px; height:38px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:15px; flex-shrink:0; overflow:hidden; }
.crm-avatar--lg { width:56px; height:56px; font-size:22px; border:3px solid rgba(255,255,255,0.8); box-shadow:0 2px 8px rgba(0,0,0,0.15); }
.crm-avatar__img { width:100%; height:100%; object-fit:cover; }
.crm-avatar--purple { background:#ede9fe; color:#7c3aed; }
.crm-avatar--blue   { background:#dbeafe; color:#1d4ed8; }
.crm-avatar--teal   { background:#ccfbf1; color:#0d9488; }
.crm-avatar--green  { background:#dcfce7; color:#15803d; }
.crm-avatar--amber  { background:#fef3c7; color:#b45309; }
.crm-avatar--rose   { background:#ffe4e6; color:#be123c; }
.crm-avatar--indigo { background:#e0e7ff; color:#4338ca; }

/* ── Detail ── */
.crm-detail { flex:1; display:flex; flex-direction:column; overflow:hidden; background:var(--gray-50,#f8fafc); }
.crm-detail__empty { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; color:var(--gray-300,#d1d5db); padding:40px; text-align:center; }
.crm-detail__empty h3 { font-size:1rem; font-weight:600; color:var(--gray-500,#6b7280); }
.crm-detail__empty p { font-size:0.82rem; color:var(--gray-400,#9ca3af); max-width:260px; line-height:1.6; }

/* ── Profile Header ── */
.crm-profile-header { position:relative; background:var(--surface,#fff); border-bottom:1px solid var(--border,#e5e7eb); padding:20px 20px 16px; flex-shrink:0; }
.crm-profile-header__bg { position:absolute; inset:0 0 50% 0; border-radius:0; }
.crm-avatar--purple + .crm-profile-header__content,
.crm-profile-header__bg { background:linear-gradient(135deg,#ede9fe,#dbeafe); }
.crm-profile-header__content { position:relative; display:flex; align-items:flex-start; gap:14px; }
.crm-profile-header__info { flex:1; min-width:0; }
.crm-profile-header__name { font-size:1.15rem; font-weight:800; color:var(--gray-900,#111827); margin-bottom:4px; }
.crm-profile-header__meta { display:flex; flex-wrap:wrap; gap:10px; font-size:0.75rem; color:var(--gray-500,#6b7280); margin-bottom:6px; }
.crm-profile-header__meta span { display:flex; align-items:center; gap:4px; }
.crm-profile-header__tags { display:flex; flex-wrap:wrap; gap:4px; margin-top:6px; }
.crm-profile-header__actions { display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }

/* ── Score bar ── */
.crm-score-bar { display:flex; align-items:center; gap:10px; padding:8px 20px; background:var(--surface,#fff); border-bottom:1px solid var(--border,#e5e7eb); flex-shrink:0; }
.crm-score-bar__label { font-size:0.72rem; color:var(--gray-500,#6b7280); white-space:nowrap; }
.crm-score-bar__track { flex:1; height:6px; background:var(--gray-100,#f3f4f6); border-radius:3px; overflow:hidden; }
.crm-score-bar__fill { height:100%; background:linear-gradient(90deg,#818cf8,#4f46e5); border-radius:3px; transition:width .4s; }
.crm-score-bar__num { font-size:0.72rem; font-weight:700; color:var(--brand-600,#4f46e5); }

/* ── Detail Tabs ── */
.crm-detail__tabs { display:flex; padding:0 16px; background:var(--surface,#fff); border-bottom:1px solid var(--border,#e5e7eb); flex-shrink:0; gap:2px; }
.crm-detail__tab { display:flex; align-items:center; gap:5px; padding:10px 12px; font-size:0.78rem; font-weight:500; color:var(--gray-500,#6b7280); border:none; background:transparent; cursor:pointer; border-bottom:2px solid transparent; transition:all .15s; margin-bottom:-1px; }
.crm-detail__tab:hover { color:var(--gray-800,#1f2937); }
.crm-detail__tab--active { color:var(--brand-600,#4f46e5); border-bottom-color:var(--brand-500,#6366f1); font-weight:600; }

/* ── Tab body ── */
.crm-tab-body { flex:1; overflow-y:auto; padding:16px; display:flex; flex-direction:column; gap:12px; }
.crm-tab-body__header { display:flex; align-items:center; justify-content:space-between; }
.crm-tab-body__title { font-size:0.84rem; font-weight:700; color:var(--gray-700,#374151); }

/* ── Note form ── */
.crm-note-form { background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:12px; padding:14px; }
.crm-note-form__label { font-size:0.75rem; font-weight:700; color:var(--gray-600,#4b5563); margin-bottom:8px; }
.crm-note-form__footer { display:flex; justify-content:flex-end; margin-top:8px; }

/* ── Timeline ── */
.crm-timeline { display:flex; flex-direction:column; gap:0; }
.crm-timeline__item { display:flex; gap:12px; }
.crm-timeline__dot-wrap { display:flex; flex-direction:column; align-items:center; width:32px; flex-shrink:0; }
.crm-timeline__dot { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; border:2px solid var(--surface,#fff); box-shadow:0 0 0 1px var(--border,#e5e7eb); background:var(--gray-50,#f8fafc); }
.crm-timeline__dot--email_in  { background:#eff6ff; }
.crm-timeline__dot--email_out { background:#eef2ff; }
.crm-timeline__dot--line_msg,
.crm-timeline__dot--line_msg_out { background:#f0fdf4; }
.crm-timeline__dot--note { background:#fffbeb; }
.crm-timeline__line { flex:1; width:1px; background:var(--border,#e5e7eb); margin:4px 0; min-height:12px; }
.crm-timeline__card { flex:1; background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:10px; padding:11px 14px; margin-bottom:12px; }
.crm-timeline__card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:5px; }
.crm-timeline__type { font-size:0.72rem; font-weight:700; }
.crm-timeline__type--email_in  { color:var(--brand-600,#4f46e5); }
.crm-timeline__type--email_out { color:#4f46e5; }
.crm-timeline__type--line_msg,
.crm-timeline__type--line_msg_out { color:#16a34a; }
.crm-timeline__type--note { color:#d97706; }
.crm-timeline__time { font-size:0.65rem; color:var(--gray-400,#9ca3af); }
.crm-timeline__subject { font-size:0.82rem; font-weight:600; color:var(--gray-700,#374151); margin-bottom:3px; }
.crm-timeline__content { font-size:0.78rem; color:var(--gray-600,#4b5563); line-height:1.5; white-space:pre-wrap; }

/* ── Opp list card ── */
.crm-opp-list-card { display:flex; background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:12px; overflow:hidden; }
.crm-opp-list-card__stage-bar { width:4px; flex-shrink:0; }
.crm-opp-list-card__stage-bar--prospect    { background:#818cf8; }
.crm-opp-list-card__stage-bar--proposal    { background:#38bdf8; }
.crm-opp-list-card__stage-bar--negotiation { background:#fbbf24; }
.crm-opp-list-card__stage-bar--closed_won  { background:#34d399; }
.crm-opp-list-card__stage-bar--closed_lost { background:#d1d5db; }
.crm-opp-list-card__body { flex:1; padding:12px 14px; }
.crm-opp-list-card__top { display:flex; align-items:center; gap:8px; margin-bottom:6px; flex-wrap:wrap; }
.crm-opp-list-card__title { font-size:0.84rem; font-weight:700; color:var(--gray-800,#1f2937); }
.crm-opp-list-card__bottom { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.crm-opp-list-card__amount { display:flex; align-items:center; gap:4px; font-size:0.78rem; font-weight:600; color:var(--gray-700,#374151); }
.crm-opp-list-card__date { display:flex; align-items:center; gap:4px; font-size:0.72rem; color:var(--gray-400,#9ca3af); }
.crm-opp-list-card__btns { display:flex; gap:4px; margin-left:auto; }
.crm-opp-list-card__notes { font-size:0.72rem; color:var(--gray-500,#6b7280); margin-top:6px; line-height:1.5; }

/* ── Followup card ── */
.crm-followup-card { display:flex; align-items:flex-start; gap:12px; background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:12px; padding:14px; }
.crm-followup-card--overdue { border-color:#fca5a5; background:#fff5f5; }
.crm-followup-card__icon { font-size:20px; flex-shrink:0; margin-top:1px; }
.crm-followup-card__body { flex:1; }
.crm-followup-card__time { font-size:0.8rem; font-weight:700; color:var(--gray-700,#374151); margin-bottom:4px; }
.crm-followup-card__time--overdue { color:#ef4444; }
.crm-followup-card__note { font-size:0.78rem; color:var(--gray-600,#4b5563); }
.crm-followup-card__actions { display:flex; gap:4px; flex-shrink:0; }

/* ── Info Grid ── */
.crm-info-grid { display:grid; grid-template-columns:1fr 1fr; gap:1px; background:var(--border,#e5e7eb); border:1px solid var(--border,#e5e7eb); border-radius:12px; overflow:hidden; margin-bottom:12px; }
.crm-info-item { background:var(--surface,#fff); padding:12px 14px; }
.crm-info-item__label { font-size:0.68rem; font-weight:700; color:var(--gray-400,#9ca3af); text-transform:uppercase; letter-spacing:.5px; margin-bottom:4px; }
.crm-info-item__val { font-size:0.84rem; color:var(--gray-800,#1f2937); }
.crm-info-item__val--mono { font-family:monospace; font-size:0.78rem; }
.crm-info-notes { background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:12px; padding:14px; }
.crm-info-notes__text { font-size:0.82rem; color:var(--gray-700,#374151); line-height:1.6; white-space:pre-wrap; margin-top:6px; }

/* ── Modals ── */
.crm-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; z-index:50; backdrop-filter:blur(4px); }
.crm-modal { background:var(--surface,#fff); border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,0.2); width:100%; max-width:460px; max-height:90vh; display:flex; flex-direction:column; }
.crm-modal--sm { max-width:360px; }
.crm-modal__header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px 14px; border-bottom:1px solid var(--border,#e5e7eb); }
.crm-modal__header h3 { font-size:1rem; font-weight:700; color:var(--gray-900,#111827); }
.crm-modal__close { width:28px; height:28px; border-radius:50%; border:none; background:var(--gray-100,#f3f4f6); color:var(--gray-500,#6b7280); font-size:16px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .15s; }
.crm-modal__close:hover { background:var(--gray-200,#e5e7eb); }
.crm-modal__body { padding:16px 20px; overflow-y:auto; display:flex; flex-direction:column; gap:14px; }
.crm-modal__footer { padding:14px 20px; border-top:1px solid var(--border,#e5e7eb); display:flex; justify-content:flex-end; gap:8px; }

/* ── Form ── */
.crm-form-row { display:flex; flex-direction:column; gap:5px; }
.crm-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.crm-label { font-size:0.75rem; font-weight:600; color:var(--gray-600,#4b5563); }
.crm-label__hint { font-weight:400; color:var(--gray-400,#9ca3af); }
.crm-label__required { color:#ef4444; }
.crm-input { padding:8px 11px; border-radius:8px; border:1px solid var(--border,#e5e7eb); font-size:0.84rem; background:var(--gray-50,#f8fafc); color:var(--gray-800,#1f2937); outline:none; transition:border-color .15s,background .15s; }
.crm-input:focus { border-color:var(--brand-400,#818cf8); background:#fff; box-shadow:0 0 0 3px rgba(129,140,248,0.12); }
.crm-textarea { padding:8px 11px; border-radius:8px; border:1px solid var(--border,#e5e7eb); font-size:0.84rem; background:var(--gray-50,#f8fafc); color:var(--gray-800,#1f2937); outline:none; resize:vertical; font-family:inherit; transition:border-color .15s; }
.crm-textarea:focus { border-color:var(--brand-400,#818cf8); background:#fff; box-shadow:0 0 0 3px rgba(129,140,248,0.12); }

/* ── Select ── */
.crm-select { padding:6px 10px; border-radius:7px; border:1px solid var(--border,#e5e7eb); font-size:0.78rem; background:var(--gray-50,#f8fafc); color:var(--gray-700,#374151); outline:none; }
.crm-select--full { width:100%; padding:8px 11px; font-size:0.84rem; }
.crm-select--status { font-size:0.76rem; padding:4px 8px; }

/* ── Badges ── */
.crm-badge { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:0.67rem; font-weight:600; }
.crm-badge--blue   { background:#dbeafe; color:#1d4ed8; }
.crm-badge--green  { background:#dcfce7; color:#15803d; }
.crm-badge--amber  { background:#fef3c7; color:#b45309; }
.crm-badge--purple { background:#ede9fe; color:#6d28d9; }
.crm-badge--gray   { background:var(--gray-100,#f3f4f6); color:var(--gray-600,#4b5563); }
.crm-badge--source { font-size:0.62rem; }
.crm-badge--count  { background:var(--gray-100,#f3f4f6); color:var(--gray-600,#4b5563); font-size:0.65rem; padding:1px 7px; }

/* ── Tags ── */
.crm-tag { display:inline-block; background:var(--brand-50,#eef2ff); color:var(--brand-700,#4338ca); border:1px solid var(--brand-100,#e0e7ff); padding:2px 9px; border-radius:20px; font-size:0.7rem; font-weight:500; }

/* ── Buttons ── */
.crm-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border-radius:8px; font-size:0.82rem; font-weight:500; cursor:pointer; border:1px solid transparent; transition:all .15s; }
.crm-btn--primary { background:var(--brand-600,#4f46e5); color:#fff; border-color:var(--brand-600,#4f46e5); }
.crm-btn--primary:hover:not(:disabled) { background:var(--brand-500,#6366f1); }
.crm-btn--primary:disabled { opacity:.5; cursor:not-allowed; }
.crm-btn--outline { background:transparent; border-color:var(--border,#e5e7eb); color:var(--gray-700,#374151); }
.crm-btn--outline:hover { background:var(--gray-50,#f8fafc); }
.crm-btn--ghost { background:transparent; border-color:transparent; color:var(--gray-600,#4b5563); }
.crm-btn--ghost:hover { background:var(--gray-100,#f3f4f6); }
.crm-btn--success { background:#dcfce7; color:#15803d; border-color:#bbf7d0; }
.crm-btn--success:hover { background:#bbf7d0; }
.crm-btn--danger { color:#ef4444; }
.crm-btn--danger:hover { background:#fff1f2; }
.crm-btn--sm { padding:5px 11px; font-size:0.78rem; }
.crm-btn--xs { padding:3px 8px; font-size:0.72rem; border-radius:6px; }

/* ── States ── */
.crm-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:40px; color:var(--gray-400,#9ca3af); font-size:0.84rem; }
.crm-loading--sm { padding:20px; }
.crm-spinner { width:24px; height:24px; border:3px solid var(--border,#e5e7eb); border-top-color:var(--brand-500,#6366f1); border-radius:50%; animation:crm-spin .7s linear infinite; flex-shrink:0; }
.crm-spinner--sm { width:18px; height:18px; border-width:2px; }
@keyframes crm-spin { to { transform:rotate(360deg); } }
.crm-empty { display:flex; flex-direction:column; align-items:center; gap:8px; padding:40px 20px; color:var(--gray-300,#d1d5db); text-align:center; }
.crm-empty p { font-size:0.84rem; color:var(--gray-400,#9ca3af); }
.crm-empty small { font-size:0.72rem; color:var(--gray-400,#9ca3af); }
.crm-empty--sm { padding:24px; }
.crm-load-more { padding:10px; border-top:1px solid var(--border,#e5e7eb); }

/* ── Dashboard ── */
.crm-dashboard { flex:1; overflow-y:auto; padding:20px; display:flex; flex-direction:column; gap:16px; }
.crm-dash-kpi { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:12px; }
.crm-dash-kpi-card { background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:14px; padding:18px 20px; display:flex; align-items:center; gap:14px; }
.crm-dash-kpi-card__icon { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.crm-dash-kpi-card__num { font-size:1.5rem; font-weight:900; color:var(--gray-900,#111827); line-height:1; margin-bottom:3px; }
.crm-dash-kpi-card__label { font-size:.75rem; color:var(--gray-500,#6b7280); margin-bottom:2px; }
.crm-dash-kpi-card__sub { font-size:.68rem; color:var(--gray-400,#9ca3af); }
.crm-dash-main { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
@media(max-width:768px){ .crm-dash-main{ grid-template-columns:1fr; } }
.crm-dash-section { background:var(--surface,#fff); border:1px solid var(--border,#e5e7eb); border-radius:14px; padding:16px; }
.crm-dash-section--full { }
.crm-dash-section__title { display:flex; align-items:center; gap:7px; font-size:.82rem; font-weight:700; color:var(--gray-700,#374151); margin-bottom:14px; }
.crm-dash-funnel { display:flex; flex-direction:column; gap:10px; }
.crm-dash-funnel__row { display:flex; align-items:center; gap:10px; }
.crm-dash-funnel__label { font-size:.72rem; font-weight:600; width:64px; flex-shrink:0; color:var(--gray-600,#4b5563); }
.crm-dash-funnel__label--closed_won { color:#15803d; }
.crm-dash-funnel__label--closed_lost { color:var(--gray-400); }
.crm-dash-funnel__track { flex:1; height:20px; background:var(--gray-100,#f3f4f6); border-radius:6px; overflow:hidden; }
.crm-dash-funnel__bar { height:100%; border-radius:6px; min-width:4px; transition:width .4s; }
.crm-dash-funnel__bar--prospect    { background:#818cf8; }
.crm-dash-funnel__bar--proposal    { background:#38bdf8; }
.crm-dash-funnel__bar--negotiation { background:#fbbf24; }
.crm-dash-funnel__bar--closed_won  { background:#34d399; }
.crm-dash-funnel__bar--closed_lost { background:#d1d5db; }
.crm-dash-funnel__meta { display:flex; gap:8px; align-items:center; flex-shrink:0; }
.crm-dash-funnel__count { font-size:.72rem; color:var(--gray-500); min-width:32px; }
.crm-dash-funnel__amount { font-size:.7rem; color:var(--gray-400); }
.crm-dash-followup { display:flex; align-items:flex-start; gap:10px; padding:10px 12px; border-radius:10px; border:1px solid var(--border,#e5e7eb); margin-bottom:8px; }
.crm-dash-followup--overdue { border-color:#fca5a5; background:#fff5f5; }
.crm-dash-followup__icon { font-size:1rem; flex-shrink:0; margin-top:1px; }
.crm-dash-followup__body { flex:1; }
.crm-dash-followup__note { font-size:.8rem; font-weight:600; color:var(--gray-700); margin-bottom:2px; }
.crm-dash-followup__time { font-size:.7rem; color:var(--gray-400); }
.crm-dash-followup__time--overdue { color:#ef4444; font-weight:600; }
.crm-dash-activity { display:flex; flex-direction:column; gap:1px; }
.crm-dash-activity__item { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:8px; transition:background .12s; }
.crm-dash-activity__item:hover { background:var(--gray-50); }
.crm-dash-activity__icon { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:.85rem; flex-shrink:0; }
.crm-dash-activity__body { flex:1; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
.crm-dash-activity__subject { font-size:.78rem; color:var(--gray-600); }
.crm-dash-activity__time { font-size:.65rem; color:var(--gray-400); flex-shrink:0; }

/* ── Range input ── */
.crm-range { width:100%; accent-color:var(--brand-500,#6366f1); height:4px; cursor:pointer; }
.w-full { width:100%; }

/* ── Activity type bar ── */
.crm-activity-type-bar { display:flex; gap:6px; margin-bottom:8px; flex-wrap:wrap; }
.crm-activity-type-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border-radius:20px; border:1px solid var(--border,#e5e7eb); background:var(--gray-50,#f8fafc); font-size:.75rem; cursor:pointer; color:var(--gray-600); transition:all .12s; }
.crm-activity-type-btn:hover { border-color:var(--brand-400,#818cf8); color:var(--brand-600,#4f46e5); background:#f0f0ff; }
.crm-activity-type-btn--active { border-color:var(--brand-500,#6366f1); background:var(--brand-50,#eef2ff); color:var(--brand-600,#4f46e5); font-weight:600; }

/* ── Contact item assignee badge ── */
.crm-contact-item__assignee { font-size:.68rem; color:var(--gray-400); margin-left:auto; white-space:nowrap; }

/* ── Item 8: 批量操作 ── */
.crm-bulk-bar { display:flex; align-items:center; gap:6px; padding:6px 12px; background:var(--brand-50,#eef2ff); border-bottom:1px solid var(--brand-200,#c7d2fe); flex-shrink:0; flex-wrap:wrap; }
.crm-bulk-bar__count { font-size:.78rem; font-weight:600; color:var(--brand-600,#4f46e5); margin-right:4px; }
.crm-bulk-dialog { display:flex; gap:6px; align-items:center; padding:6px 12px; background:var(--gray-50); border-bottom:1px solid var(--border,#e5e7eb); flex-shrink:0; }
.crm-list-header { display:flex; align-items:center; padding:6px 12px; border-bottom:1px solid var(--border,#e5e7eb); background:var(--gray-50); flex-shrink:0; }
.crm-list-header__text { font-size:.72rem; color:var(--gray-500); margin-left:6px; }
.crm-checkbox-wrap { display:inline-flex; align-items:center; cursor:pointer; }
.crm-checkbox-wrap input[type=checkbox] { width:14px; height:14px; cursor:pointer; accent-color:var(--brand-500,#6366f1); }
.crm-contact-item__check { padding:0 6px 0 0; flex-shrink:0; }
.crm-contact-item--checked { background:var(--brand-50,#eef2ff); }
.crm-input--sm { padding:4px 8px; font-size:.78rem; border-radius:6px; border:1px solid var(--border,#e5e7eb); background:var(--surface,#fff); flex:1; }
.crm-select--sm { padding:4px 8px; font-size:.78rem; border-radius:6px; border:1px solid var(--border,#e5e7eb); background:var(--surface,#fff); }

/* ── Item 9: 自定義欄位 ── */
.crm-custom-admin { border:1px solid var(--border,#e5e7eb); border-radius:10px; margin-bottom:16px; overflow:hidden; }
.crm-custom-admin__head { padding:8px 12px; background:var(--gray-50); border-bottom:1px solid var(--border); }
.crm-custom-admin__title { font-size:.78rem; font-weight:600; color:var(--gray-600); }
.crm-custom-admin__row { display:flex; align-items:center; gap:8px; padding:8px 12px; border-bottom:1px solid var(--border); }
.crm-custom-admin__name { font-size:.8rem; font-weight:600; color:var(--gray-700); flex:1.5; }
.crm-custom-admin__key { font-size:.72rem; color:var(--gray-400); font-family:monospace; flex:1; }
.crm-custom-admin__add { display:flex; gap:6px; align-items:center; padding:8px 12px; flex-wrap:wrap; }
.crm-custom-admin__opts { padding:0 12px 8px; }
.crm-custom-field-row { display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid var(--border,#e5e7eb); }
.crm-custom-field-row__label { font-size:.8rem; font-weight:600; color:var(--gray-600); min-width:100px; flex-shrink:0; }
.crm-custom-field-row__input { flex:1; }

/* ── Item 10: AI 摘要 ── */
.crm-ai-summary { margin:0 0 0; border-bottom:1px solid var(--border,#e5e7eb); padding:10px 16px; background:linear-gradient(135deg,#f8faff 0%,#f0f4ff 100%); flex-shrink:0; }
.crm-ai-summary__header { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
.crm-ai-summary__title { font-size:.78rem; font-weight:700; color:var(--brand-700,#3730a3); flex:1; }
.crm-ai-summary__date { font-size:.68rem; color:var(--gray-400); }
.crm-ai-summary__list { margin:0 0 6px 16px; padding:0; font-size:.78rem; color:var(--gray-700); }
.crm-ai-summary__list li { margin-bottom:3px; }
.crm-ai-summary__concern { font-size:.75rem; color:var(--gray-600); margin-bottom:3px; }
.crm-ai-summary__commitment { font-size:.75rem; color:var(--color-warning,#d97706); }
.crm-ai-summary__concern-label { font-weight:600; }
.crm-ai-summary__empty { font-size:.75rem; color:var(--gray-400); font-style:italic; }
.crm-ai-summary__loading { display:flex; align-items:center; gap:8px; font-size:.75rem; color:var(--gray-500); padding:4px 0; }
.mt-2 { margin-top:8px; }

/* ── Item 11: AI 成交機率 ── */
.crm-opp-card__ai-prob { display:flex; align-items:center; gap:4px; font-size:.68rem; margin-bottom:3px; }
.crm-opp-card__ai-prob-label { color:var(--gray-400); }
.crm-opp-card__ai-prob-val { font-weight:700; }
.crm-opp-ai-result { margin-top:6px; padding:6px 8px; background:var(--gray-50); border-radius:6px; border-left:3px solid var(--brand-300,#a5b4fc); }
.crm-opp-ai-result__prob { font-size:.75rem; font-weight:700; display:block; margin-bottom:2px; }
.crm-opp-ai-result__risk { font-size:.72rem; color:#b45309; display:block; }

/* ── Item 12: AI 下一步建議 ── */
.crm-ai-summary__next-steps { margin-top:8px; padding-top:8px; border-top:1px dashed var(--border,#e5e7eb); }
.crm-ai-summary__steps-list { margin:0; padding-left:18px; }
.crm-ai-summary__steps-list li { font-size:.75rem; color:var(--brand-700,#3730a3); margin-bottom:3px; }

/* ── Item 13: CRM 發信 Modal ── */
.crm-modal--md { width:560px; max-width:95vw; }
</style>
